// Free downloadable templates (/templates/<slug>). Each one is a real file in
// public/downloads/templates/ that anyone can download without an email. The
// One Person Agency YouTube channel links here from its videos, with UTM
// parameters that the site's attribution cookie keeps (plugins/conversion-tracking.client.ts).
//
// To add a template: put the PDF/DOCX in public/downloads/templates/ (the
// /downloads/* path is noindex, see netlify.toml), a page-1 preview WebP in
// public/images/templates/ (then `node scripts/template-preview-variants.mjs` for its
// 320w/640w copies), append an entry below, and register
// /templates/<slug> in data/seo-routes.ts, data/internal-links.ts and
// public/llms.txt, then run the localization pipeline (AGENTS.md).
//
// Copy rules: no plan-gated claims unless they are true on the live pricing
// (data/pricing-features.json), and any Workspace369 recommendation carries the
// line "Our #1 pick is Workspace369." (Szymon, Oct 17: no "and we make it").

export interface FreeTemplateFile {
  format: 'pdf' | 'docx';
  label: string;
  href: string;
  size: string;
}

export interface FreeTemplateSection {
  title: string;
  copy: string;
}

// A law that sets rules for the contract the template produces. Every field is
// taken from the official source it links to (no secondary summaries).
export interface FreeTemplateLawRule {
  place: string;
  law: string;
  inForce: string;
  threshold: string;
  contract: string;
  payment: string;
  sources: { label: string; href: string }[];
}

// A retired free resource this template replaces: what its old link shows now
// (checked on the block's checkedOn date) and a Wayback snapshot of the original.
export interface FreeTemplateRetiredResource {
  name: string;
  oldUrl: string;
  was: string;
  now: string;
  archiveHref: string;
  archivedOn: string;
}

export interface FreeTemplate {
  slug: string;
  updatedOn: string;
  // How the sections are ordered, for the line above the list (default: 'in the order a client reads them').
  sectionsOrder?: string;
  cardTitle: string;
  cardCopy: string;
  h1: string;
  metaTitle: string;
  description: string;
  lede: string;
  pages: number;
  preview: { src: string; width: number; height: number; alt: string };
  files: FreeTemplateFile[];
  sections: FreeTemplateSection[];
  example: { title: string; copy: string };
  steps: string[];
  product: { copy: string; linkLabel: string; path: string };
  faq: { q: string; a: string }[];
  relatedLinks: { label: string; path: string }[];
  lawRules?: { title: string; intro: string; checkedOn: string; rules: FreeTemplateLawRule[]; note: string };
  retiredResources?: { title: string; intro: string; checkedOn: string; items: FreeTemplateRetiredResource[]; note: string };
  // Renders the shared "Proposal vs contract" section (data/proposal-vs-contract.ts) just before the FAQ.
  proposalVsContract?: boolean;
}

export const freeTemplates: FreeTemplate[] = [
  {
    slug: 'web-design-proposal',
    updatedOn: '2026-10-13',
    cardTitle: 'Web design proposal template',
    cardCopy: 'Eight sections, three packages and a filled-in example for a six-page website. PDF and Word.',
    h1: 'Free web design proposal template (PDF and Word).',
    metaTitle: 'Web Design Proposal Template: Free PDF & Word | Workspace369',
    description:
      'Download a free web design proposal template in PDF and Word: scope, three packages, timeline, deposit, payment terms and sign-off. No email needed.',
    lede:
      'A proposal for a website project that a client can say yes to: what is included, what each package costs, when it goes live and what you need from them. Fill in the brackets, delete the guidance and send it.',
    pages: 7,
    preview: {
      src: '/images/templates/web-design-proposal-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design proposal template: a "Website Proposal for [Client business name]" header with prepared for, prepared by, date and valid-until lines, then the "What this project is" and "Scope" sections.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-proposal-template.pdf', size: '173 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-proposal-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'What this project is', copy: 'Two or three sentences in the client’s own words: what the site is for, who it is for and how you will both know it worked.' },
      { title: 'Scope', copy: 'The pages included, what every package covers, and exactly what one revision round means, so the project cannot quietly grow.' },
      { title: 'Packages', copy: 'Three options that differ by pages, rounds and extras, not only by price, plus optional add-ons and the one you recommend.' },
      { title: 'Timeline', copy: 'Dates for your side and the client’s side, with a line that moves launch if copy, photos or feedback arrive late.' },
      { title: 'Price and payment terms', copy: 'The fixed fee, a deposit on acceptance, an optional milestone, the balance before launch, ownership and third-party costs.' },
      { title: 'What is not included, and change requests', copy: 'What sits outside the fee, and how extra pages or rounds get a short written quote before any work starts.' },
      { title: 'What we need from you', copy: 'One person who gives final feedback, plus deadlines for copy, brand files, photos and logins.' },
      { title: 'Acceptance', copy: 'Package and add-ons chosen, a signature or typed name, and the deposit that books the start date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same proposal completed for a fictional dental practice: a six-page Webflow site, three packages from $3,200 to $6,900, a 50% deposit and a dated four-week timeline. Use it to see what “specific” looks like; your numbers will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Replace every [bracket] with your project’s details and delete the grey guidance lines.',
      'Price the three packages from your own hours and rates, and tick the one you recommend.',
      'Send it as a PDF or a link, then follow up after three to five working days if you have not heard back.',
    ],
    product: {
      copy: 'Sending proposals every week? Workspace369 Proposals turn this outline into a link your client reads, accepts and signs online.',
      linkLabel: 'See Workspace369 Proposals',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the web design proposal template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'Can I edit the template in Google Docs?',
        a: 'Yes. Upload the Word (.docx) file to Google Drive and open it with Google Docs. It also opens in Microsoft Word, Apple Pages and LibreOffice.',
      },
      {
        q: 'What should a web design proposal include?',
        a: 'The project goal, the pages and features in scope, two or three packages with prices, a timeline that shows the client’s deadlines too, the deposit and payment terms, what is not included, what you need from the client, and how to accept.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your payment, ownership and late-fee terms checked by someone qualified where you work, and pair the proposal with a contract.',
      },
    ],
    relatedLinks: [
      { label: 'Web design quote template', path: '/templates/web-design-quote' },
      { label: 'How to write a web design proposal', path: '/learn/web-design-proposal-template' },
      { label: 'Web design contract template', path: '/templates/web-design-contract' },
      { label: 'Web design pricing benchmarks', path: '/research/web-design-pricing-benchmarks' },
      { label: 'Deposit calculator', path: '/tools/deposit-calculator' },
      { label: 'Proposal follow-up email generator', path: '/tools/proposal-follow-up-generator' },
      { label: 'Web design proposal follow-up email template', path: '/templates/web-design-proposal-follow-up-emails' },
      { label: 'Web design revision policy template', path: '/templates/web-design-revision-policy' },
    ],
  },
  {
    slug: 'website-maintenance-agreement',
    updatedOn: '2026-10-17',
    cardTitle: 'Website maintenance agreement template',
    cardCopy: 'A monthly care plan: what it covers, the fee, response times, accounts and cancelling. PDF and Word.',
    h1: 'Free website maintenance agreement template (PDF and Word).',
    metaTitle: 'Website Maintenance Agreement Template: Free PDF & Word',
    description:
      'Download a free website maintenance agreement in PDF and Word: monthly tasks, edit hours, fee and billing, response times and ownership. No email needed.',
    lede:
      'A monthly care plan a client can read in five minutes: what you do each month, what costs extra, what it costs, how fast you reply and who owns the accounts. Fill in the brackets, delete the guidance and send it.',
    pages: 5,
    preview: {
      src: '/images/templates/website-maintenance-agreement-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the website maintenance agreement template: a "Website Care Plan for [Client business name]" header with prepared for, prepared by, date, site covered and start lines, then the parties section and a list of what the plan covers each month.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/website-maintenance-agreement-template.pdf', size: '134 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/website-maintenance-agreement-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'The parties and the plan', copy: 'Who looks after which site, for whom, and that the plan runs month to month.' },
      { title: 'What the plan covers each month', copy: 'Backups before updates, checks that forms and key pages still work, a set number of edit hours, and fixing anything an update you ran breaks.' },
      { title: 'What is not covered, and change requests', copy: 'New pages, redesigns, copy, photos and third-party fees sit outside the plan, and extra work gets a written quote before it starts.' },
      { title: 'Fee and billing', copy: 'The monthly fee, when it is billed and due, what happens when a payment is late, and a simple way to price the plan from your own hours.' },
      { title: 'Response times', copy: 'How fast you reply, what counts as urgent, your working hours, and a line that you do not run the hosting.' },
      { title: 'Accounts and access', copy: 'A table of who owns the domain, the platform plan and the site admin, with your own login instead of the client’s password.' },
      { title: 'Cancelling and handover', copy: 'The notice period, what you hand over on the last day, and when the plan ends.' },
      { title: 'Acceptance', copy: 'A signature or typed name and the date the first monthly invoice goes out.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same care plan completed for a fictional dental practice on Squarespace: backups and checks, 2 hours of edits a month, $240 a month billed in advance, replies within 2 business days and 30 days’ notice to cancel. The figures are made up; price yours from your own hours.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'List the work you actually do each month and delete what you do not offer.',
      'Set the included hours and price the plan from your hours and rate, using the formula in section 4.',
      'Fill in the accounts table with the client before you start, then send the plan for a signature or typed name.',
    ],
    product: {
      copy: 'Billing care plans every month? Workspace369 can hold them as retainers with a recurring invoice.',
      linkLabel: 'See retainers and recurring billing',
      path: '/features/retainers-and-recurring-billing',
    },
    faq: [
      {
        q: 'Is the website maintenance agreement template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should a website maintenance agreement include?',
        a: 'The work you do each month, how many hours of edits are included, what is not covered, the monthly fee and billing terms, response times, who owns the domain and accounts, and how either side can cancel.',
      },
      {
        q: 'How much should I charge for website maintenance?',
        a: 'Price it from your own hours: add the fixed monthly hours for backups and checks to the included edit hours, then multiply by your hourly rate. The template walks through the math; there is no standard rate to copy.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your payment, ownership, liability and late-fee wording checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Retainer agreement template guide', path: '/learn/retainer-agreement-template' },
      { label: 'Retainer calculator', path: '/tools/retainer-calculator' },
      { label: 'Fixed fee vs hourly vs retainer billing', path: '/blog/fixed-fee-vs-hourly-vs-retainer-billing' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
    ],
  },
  {
    slug: 'web-design-client-questionnaire',
    updatedOn: '2026-10-17',
    cardTitle: 'Web design client questionnaire template',
    cardCopy: 'Eight short sections to send before the kickoff call: goal, style examples, content dates and access. PDF and Word.',
    h1: 'Free web design client questionnaire template (PDF and Word).',
    metaTitle: 'Web Design Client Questionnaire: Free PDF & Word',
    description:
      'Download a free web design client questionnaire in PDF and Word: the business, goal, style, content, due dates, pages, access and feedback. No email needed.',
    lede:
      'The questions to send before the kickoff call, so the call is for decisions instead of collecting basics. Each one asks for an example, a name or a date, not an adjective.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-client-questionnaire-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design client questionnaire template: a "Website Questionnaire for [Client business name]" header with a return-by date, then numbered questions about the business and a tick list for the main action visitors should take.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-client-questionnaire-template.pdf', size: '134 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-client-questionnaire-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'About the business', copy: 'What they sell, who their customers are, what makes them different and the one job the site must do.' },
      { title: 'The goal', copy: 'The one action a visitor should take, and how you will both know the site worked.' },
      { title: 'Style: examples, not adjectives', copy: 'Three sites they like and one they do not, with a line on why, instead of “clean and modern”.' },
      { title: 'Content and dates', copy: 'Who provides the copy, logo and photos, by when, and what happens to the launch date if they are late.' },
      { title: 'Pages and features', copy: 'Tick boxes for pages and features, so the answers match your proposal.' },
      { title: 'Access and ownership', copy: 'Who owns the website account and the domain, and who can invite you, without anyone sending a password.' },
      { title: 'Feedback and decisions', copy: 'The one person who gives final feedback, and how quickly feedback comes back.' },
      { title: 'Anything else', copy: 'Deadlines the site must meet, things they do not want, and anything you forgot to ask.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same questionnaire answered by a fictional dental practice: booking a first visit online as the goal, three liked sites with a reason each, six pages, content due on the 5th and one named person for feedback.',
    },
    steps: [
      'Download the PDF to send as it is, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Put your return-by date at the top and delete any section that does not fit the project.',
      'Send it before the kickoff call, with a shared folder link for files.',
      'Use the answers to fill in your proposal and scope of work.',
    ],
    product: {
      copy: 'Sending this to every new client? Workspace369 can host it as an intake form on your site: submissions land in your Requests inbox and one click turns a request into a client.',
      linkLabel: 'See intake forms in Workspace369',
      path: '/solutions/client-intake-form-software',
    },
    faq: [
      {
        q: 'Is the web design client questionnaire free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'Can I turn the questionnaire into an online form?',
        a: 'Yes. The questions work in Google Forms, Typeform or any form tool. For files, a shared folder link is simpler, since Google Forms asks responders to sign in to answer a file upload question.',
      },
      {
        q: 'What should I ask a web design client before starting?',
        a: 'The goal of the site, examples of sites they like and why, who provides each piece of content and by when, the pages and features they need, who owns the domain and accounts, and who gives final feedback.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have anything contractual, such as ownership, payment or who is responsible for content, checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Client intake form template guide', path: '/learn/client-intake-form-template' },
      { label: 'Web design client onboarding checklist template', path: '/templates/web-design-client-onboarding-checklist' },
      { label: 'Web design discovery call questions template', path: '/templates/web-design-discovery-call-questions' },
      { label: 'Website scope of work template', path: '/templates/website-scope-of-work' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
    ],
  },
  {
    slug: 'website-scope-of-work',
    updatedOn: '2026-10-17',
    cardTitle: 'Website scope of work template',
    cardCopy: 'Deliverables you can count, acceptance criteria, what is not included and a dated timeline. PDF and Word.',
    h1: 'Free website scope of work template (PDF and Word).',
    metaTitle: 'Website Scope of Work Template: Free PDF & Word',
    description:
      'Download a free website scope of work template in PDF and Word: goal, deliverables, acceptance criteria, exclusions, deadlines and sign-off. No email needed.',
    lede:
      'The page that says exactly what you will build and when it counts as done. Attach it to your proposal or contract, or paste it in as the scope section.',
    pages: 5,
    preview: {
      src: '/images/templates/website-scope-of-work-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the website scope of work template: a "Scope of Work: Website for [Client business name]" header with prepared for, prepared by, date and attached-to lines, then the goal section and a numbered list of pages under deliverables.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/website-scope-of-work-template.pdf', size: '137 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/website-scope-of-work-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Goal', copy: 'Two or three sentences on what the site is for, who it is for and one outcome you can check.' },
      { title: 'Deliverables', copy: 'Every page and feature by name, so a stranger could count them, and a line that anything unlisted is a change request.' },
      { title: 'Acceptance criteria', copy: 'The checks that must pass before the work is finished, plus a review window for the client’s answer.' },
      { title: 'Assumptions', copy: 'What you are counting on, such as content arriving on time and one person approving.' },
      { title: 'Not included', copy: 'Copywriting, photos, hosting fees, support after launch and anything else outside the scope, named up front.' },
      { title: 'What we need from the client', copy: 'A dated list of the client’s side: copy, logo, photos, access and consolidated feedback.' },
      { title: 'Timeline', copy: 'Each step with who owns it and a target date, from content due to launch.' },
      { title: 'Changes and sign-off', copy: 'How extra pages or rounds get a written quote first, and a signature or typed name to agree the scope.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same scope completed for a fictional dental practice: six named pages on Webflow, a booking request form, three acceptance checks, a 5-day review window, two feedback rounds and a dated timeline to launch.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Name every page and feature in section 2, and delete anything you are not delivering.',
      'Write acceptance checks you could test, and date the client’s side as well as yours.',
      'Attach it to your proposal or contract and get it signed before work starts.',
    ],
    product: {
      copy: 'Sending lots of these? Workspace369 Proposals turn your scope into a link your client accepts with a typed name and pays the deposit on.',
      linkLabel: 'See Workspace369 Proposals',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the website scope of work template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What is the difference between a proposal and a scope of work?',
        a: 'A proposal sells the project: options, prices and why you. A scope of work defines it: exactly what gets delivered, how it is accepted and what is not included. Many designers attach the scope to the proposal or paste it in as the scope section.',
      },
      {
        q: 'What are acceptance criteria in a website project?',
        a: 'A short list of checks that must be true before the work counts as finished, such as every page being live, the contact form reaching the client’s inbox and pages displaying correctly on the browsers you name.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Whether a scope of work forms part of your contract depends on where you work, so have acceptance, ownership and payment wording checked by someone qualified.',
      },
    ],
    relatedLinks: [
      { label: 'Website project timeline template', path: '/templates/website-project-timeline' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'How to stop scope creep on client projects', path: '/blog/how-to-stop-scope-creep-on-client-projects' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Web design revision policy template', path: '/templates/web-design-revision-policy' },
    ],
  },
  {
    slug: 'website-change-request-form',
    updatedOn: '2026-10-19',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Website change request form template',
    cardCopy: 'A one-page form to price a change against the original scope, plus four reply emails. PDF and Word.',
    h1: 'Free website change request form template (PDF and Word).',
    metaTitle: 'Website Change Request Form: Free PDF & Word',
    description:
      'Download a free website change request form in PDF and Word: check the ask against scope, show the added fee and days, get approval, plus four reply emails.',
    lede:
      'When a client asks for more after they said yes, this form sorts the request into included, a fix or new work, and puts a price and a new launch date on it before you start.',
    pages: 5,
    preview: {
      src: '/images/templates/website-change-request-form-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the website change request form template: a "Part 1: Change Request Form" header with project, change request number, date and original proposal lines, three tick boxes for included, a fix or new work, and a table of the added fee, pages and business days.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/website-change-request-form-template.pdf', size: '129 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/website-change-request-form-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'What the client asked for', copy: 'One request per form, in the client’s own words, with the date and how it arrived.' },
      { title: 'Checked against the original scope', copy: 'Sort the request into already included, a fix you owe them, or new work outside the scope.' },
      { title: 'What it adds', copy: 'A small table of the original fee, pages and days, what the change adds and the new totals, plus the new launch date.' },
      { title: 'The client’s options', copy: 'Approve it, take a smaller version with its own price, or hold it for after launch.' },
      { title: 'Payment', copy: 'The fee for this change, invoiced on its own so the original invoices stay unchanged.' },
      { title: 'Feedback rounds and big changes', copy: 'When the included rounds apply, and the line past which you write a new proposal instead.' },
      { title: 'Approval', copy: 'The option chosen and a signature or typed name, so work starts after a written yes.' },
      { title: 'Four reply emails', copy: 'Short replies for new work, something already included, a bug you will fix, and “that costs too much”.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional bakery asks for an online ordering page and more homepage tweaks. The form marks the ordering page as new work at $650 and 4 business days, the tweaks as included in round 2, and offers a $150 order-by-email form as the smaller option.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Open the accepted proposal and check the request against its scope before you price anything.',
      'Fill in the fee and days the change adds, and offer a smaller version instead of a discount.',
      'Send it with the matching reply email and start only after the client approves in writing.',
    ],
    product: {
      copy: 'In Workspace369, an accepted proposal locks, and on Specialist a later change goes through a change order the client approves by link.',
      linkLabel: 'See change orders',
      path: '/features/change-orders',
    },
    faq: [
      {
        q: 'Is the website change request form free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'When should I use a change request instead of a new proposal?',
        a: 'Use a change request for an addition to the agreed project. If a change is bigger than a line you set in advance, for example 10% of the budget or $1,000, whichever is greater, write a new proposal instead.',
      },
      {
        q: 'Is a bug fix a change request?',
        a: 'No. If something you built does not work as agreed, it is a fix you make at no charge. The form has a separate tick box for it so it is not priced by mistake.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your payment, ownership and late-fee wording checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Say no to a client: reply templates', path: '/templates/say-no-to-a-client-replies' },
      { label: 'Web design revision policy template', path: '/templates/web-design-revision-policy' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Client change request template guide', path: '/blog/client-change-request-template' },
      { label: 'Scope-change request builder', path: '/tools/scope-change-request-generator' },
    ],
  },
  {
    slug: 'web-design-deposit-request',
    updatedOn: '2026-10-25',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design deposit request template',
    cardCopy: 'Deposit terms, a payment schedule, the request, invoice and reminder emails, and three pushback replies. PDF and Word.',
    h1: 'Free web design deposit request template (PDF and Word).',
    metaTitle: 'Web Design Deposit Request: Free PDF & Word',
    description:
      'Download a free web design deposit request template in PDF and Word: deposit terms, a payment schedule, request, invoice and reminder emails and an example.',
    lede:
      'Everything you need to ask a client for a deposit without apologising: terms that go in the proposal, a schedule that adds up, and emails with a date, an amount and a pay link.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-deposit-request-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design deposit request template: deposit terms with the amount, due date and start date, two choices for how the deposit is used, a cancellation clause, and a payment schedule table with deposit, design approval, balance and total rows.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-deposit-request-template.pdf', size: '127 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-deposit-request-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'Deposit terms', copy: 'A clause for your proposal: the amount or percentage, when it is due, the start date it books, and that work starts when it arrives. You choose whether the deposit pays for the first phase or is held and applied to the final invoice, and write down what is refunded if the project is cancelled before or after work starts.' },
      { title: 'Payment schedule', copy: 'Two layouts with real dates: deposit and balance, or deposit, design approval and balance before launch. A total row, so you can check every payment adds up, and a line for when invoices are due.' },
      { title: 'The deposit request email', copy: 'Sent the day the client says yes: the start date it books, the amount, the date it is due and the pay link in the email itself, not in an attachment. No “sorry” and no “whenever you get a chance”.' },
      { title: 'The deposit invoice email', copy: 'One number, one date, one link, with the invoice number and due date in the subject line. The invoice itself carries a real due date and the way to pay.' },
      { title: 'The reminder', copy: 'A short note the day after the due date, about the invoice only: the amount, the date it was due, and that the start date is held until it is paid.' },
      { title: 'Replies to three pushbacks', copy: 'What to say to “Can I pay after the design?” (offer a split, not a discount), “Can you start now? I’ll pay Friday” (hold the kickoff call, start design when it lands) and “Is it refundable?” (point to the clause they agreed to).' },
      { title: 'Checklist before you send', copy: 'Six checks: the terms are in the proposal, the amount, due date and start date are written down, the pay link is in the email, the refund rule is written, the payments add up, and the reminder is set.' },
      { title: 'Acceptance', copy: 'A line for under your proposal’s signature block: choose a package, sign or type your name, and pay the deposit to book the start date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same deposit request for a fictional $4,800 website: a 50% deposit of $2,400 due within 3 business days, the balance before launch, the three emails written out and a three-payment split for a client who asks to pay later.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Paste the deposit terms into your proposal, so the client agrees to them before saying yes.',
      'Pick your percentage and dates, and check that the payment schedule adds up to the total.',
      'Send the request email the day the client accepts, and set the reminder for the day after the due date.',
    ],
    product: {
      copy: 'Sending lots of these? Workspace369 Proposals turn this into a link your client accepts with a typed name and pays the deposit on.',
      linkLabel: 'See Workspace369 Proposals',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the web design deposit request template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How much deposit should a web designer ask for?',
        a: 'There is no standard percentage; no professional body, including AIGA, sets one. Pick an amount that covers the time you book for the client, write it in the proposal and make sure the payments add up to the total.',
      },
      {
        q: 'Should a web design deposit be refundable?',
        a: 'Decide before you send the proposal and write it down. One approach is a full refund before work starts and a refund less the work done after that. Have the wording checked where you work.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your deposit, refund and late-payment wording checked by someone qualified where you work.',
      },
      {
        q: 'When should I send the deposit request?',
        a: 'The same day the client says yes, while the decision is fresh. Give it a due date a few days out, name the start date it books, and put the pay link in the email.',
      },
      {
        q: 'What if the client wants to pay the deposit after the design?',
        a: 'Offer a split instead of a delay or a discount: part now, part at design approval and the rest before launch. Check the payments still add up to the total, and keep the start date tied to the first payment.',
      },
      {
        q: 'Should the deposit go toward the first or the last invoice?',
        a: 'Either works if it is written down. The template has both: the deposit pays for the first phase, or it is held and applied to the final invoice. Pick one before you send the proposal.',
      },
    ],
    relatedLinks: [
      { label: 'Deposit calculator', path: '/tools/deposit-calculator' },
      { label: 'How to take a deposit before starting work', path: '/blog/how-to-take-a-deposit-before-starting-work' },
      { label: 'Deposit invoice guide', path: '/learn/deposit-invoice' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Web design invoice template', path: '/templates/web-design-invoice' },
    ],
  },
  {
    slug: 'web-design-revision-policy',
    updatedOn: '2026-10-25',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design revision policy template',
    cardCopy: 'A revision clause for your proposal, what counts as a round, pricing an extra round and four reply emails. PDF and Word.',
    h1: 'Free web design revision policy template (PDF and Word).',
    metaTitle: 'Web Design Revision Policy: Free PDF & Word',
    description:
      'Download a free web design revision policy template in PDF and Word: a revision clause, rounds by package, feedback process, extra rounds and four replies.',
    lede:
      'A clause that puts a number on revisions, so the project has an end. Paste it into your proposal, then use the reply emails when a client asks for one more round.',
    pages: 4,
    preview: {
      src: '/images/templates/web-design-revision-policy-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design revision policy template: a revision clause with two rounds, one named person and a five-day feedback window, then a table showing what counts as a round, a fix, not a round yet or a change request.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-revision-policy-template.pdf', size: '124 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-revision-policy-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'The revision clause', copy: 'The number of rounds, such as one on the design and one on the built site; one consolidated list of feedback from one named person per round; a feedback window, with the schedule moving if notes arrive late; and a written quote for anything past the last round. It warns against “unlimited revisions”, because without a number the client decides when the project ends.' },
      { title: 'What counts as a round', copy: 'A table with what the client sends, what it is and what you do: one list of notes is a round, a typo or something broken is a fix at no charge, notes arriving one by one are not a round yet, and a new page or direction is a change request.' },
      { title: 'Rounds in your package table', copy: 'More rounds in bigger packages, plus the price of an extra round in each, so a client who wants more moves up a package instead of asking for a favor.' },
      { title: 'The feedback process', copy: 'Four steps: send the draft with the date feedback is due, get one list from the named person, confirm it and send the next version, then ask for written approval after the last round.' },
      { title: 'Pricing an extra round', copy: 'Hours an extra round adds times your hourly rate. The worked example shows a site whose hourly pay drops as unpaid rounds pile up, and how pricing each extra round brings it back. The client only sees the price.' },
      { title: 'Four reply emails', copy: 'Short replies for another round (the price and the days it adds), a tiny tweak (fixed at no charge), conflicting notes from two people (one combined list, please) and a whole new direction (a quote, not a round).' },
      { title: 'Change requests', copy: 'Where a new page or feature goes instead: the change request form, with the change, the extra time, the price and a place to approve.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same policy for a fictional bakery’s website: two rounds, one named person, a 5-day feedback window, three packages from $3,200 to $6,900 and an extra round at $400 and 3 business days. The prices are made up, not typical rates.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Set your number of rounds and the feedback window, and paste the clause into your proposal.',
      'Price an extra round from the hours it adds and put it in your package table.',
      'When a client asks for more, send the matching reply email, and use the change request form for new work.',
    ],
    product: {
      copy: 'Sending lots of proposals? Workspace369 Proposals keep the rounds in the proposal your client accepts online.',
      linkLabel: 'See Workspace369 Proposals',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the web design revision policy template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How many revision rounds should a web designer include?',
        a: 'Pick a number per package and write it down; two rounds, one on the design and one on the built site, is a simple starting point. More rounds belong to a bigger package rather than a favor.',
      },
      {
        q: 'What counts as a revision round?',
        a: 'One consolidated list of feedback from one named person on a draft. A typo or something broken is a fix, notes arriving one by one are not a round yet, and a new page or direction is a change request.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your acceptance, payment and ownership wording checked by someone qualified where you work.',
      },
      {
        q: 'How do I price an extra revision round?',
        a: 'Estimate the hours one more round adds and multiply by your hourly rate. If a round takes about 4 hours and you charge $100 an hour, an extra round is $400. Add the days it adds to the timeline, and put the price in your package table before the project starts.',
      },
      {
        q: 'Should I offer unlimited revisions?',
        a: 'No. Without a number, the client decides when the project is done. Name the rounds per package and quote anything beyond them.',
      },
      {
        q: 'What if two people send different feedback?',
        a: 'It is not a round yet. Ask the named person for one combined list by a date, and start the round when it arrives.',
      },
    ],
    relatedLinks: [
      { label: 'Say no to a client: reply templates', path: '/templates/say-no-to-a-client-replies' },
      { label: 'Difficult client reply scripts', path: '/templates/difficult-client-reply-scripts' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'How to stop scope creep on client projects', path: '/blog/how-to-stop-scope-creep-on-client-projects' },
    ],
  },
  {
    slug: 'web-design-proposal-follow-up-emails',
    updatedOn: '2026-10-25',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design proposal follow-up email template',
    cardCopy: 'Three follow-up emails for day 3, 7 and 14, replies for every answer and a follow-up log. PDF and Word.',
    h1: 'Free web design proposal follow-up email template (PDF and Word).',
    metaTitle: 'Web Design Proposal Follow-Up Emails: Free PDF & Word',
    description:
      'Download a free web design proposal follow-up email template in PDF and Word: a sending schedule, emails for day 3, 7 and 14, and replies for yes and no.',
    lede:
      'For the proposal the client has not answered: three short emails that give them an easy reply, then stop. Each one goes out as a reply to your original proposal email.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-proposal-follow-up-email-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design proposal follow-up email template: a schedule table from proposal sent on day 0 to emails on day 3, 7 and 14 and a later check-in, the check-in line for your proposal, and the start of email 1.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-proposal-follow-up-email-template.pdf', size: '122 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-proposal-follow-up-email-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'When to send', copy: 'A schedule from the day you send the proposal (day 0) to emails on day 3, 7 and 14, then one check-in about three months later, with a column for the real dates. It is a rule of thumb, not a study: bend it to your client and your sales cycle.' },
      { title: 'The line for your proposal', copy: 'One sentence that tells the client when you will check in, such as next Thursday, and invites questions before then, so the follow-up is something they expected rather than a nag.' },
      { title: 'Email 1, day 3', copy: 'A plain subject line with the client’s business and their start date, then a reply by number: 1 to go ahead, 2 for a 15-minute call, 3 if it is not the right time. It also asks whether price, timing or scope is the snag.' },
      { title: 'Email 2, day 7', copy: 'The date the proposal is valid until, your smallest package with its price and the one thing it covers that the client cared about most, and your standard package beside it. A client stuck on price may not say so; a smaller option says it for them.' },
      { title: 'Email 3, day 14', copy: 'A subject line that asks whether to close the proposal, the date you will close it, and one easy way back in: reply “keep it open” and you hold their start date.' },
      { title: 'When they reply', copy: 'A short answer for each reply: for yes, the acceptance link and the start date; for later, a month to check back; for a call, two times to choose from; for no, one question about what would have made it a yes, asked once.' },
      { title: 'When to stop', copy: 'Stop after the third email, or the same day the client asks you to. The template includes a general-information note on the US CAN-SPAM opt-out rule, and one calendar check-in about three months later with something new to say.' },
      { title: 'Follow-up log', copy: 'One row per proposal with the date of each email, the reply and the next step, so you always know which email you are on.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same sequence for a fictional dental practice’s $4,800 proposal: emails on the 4th, 8th and 15th, the client’s reply of “3”, and a single check-in set for January.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Add the check-in line to your proposal and write the real dates into the schedule when you send it.',
      'Send each email as a reply to your original proposal email.',
      'Stop after the third email, or the same day the client asks you to.',
    ],
    product: {
      copy: 'Sending lots of proposals? In Workspace369, clients open and accept a proposal through a link, and Workspace369 can send up to three reminder emails, 48 hours apart, to a client who has viewed a proposal but not responded.',
      linkLabel: 'See Workspace369 Proposals',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the proposal follow-up email template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How long should I wait to follow up on a proposal?',
        a: 'A simple rule of thumb is day 3, day 7 and day 14 after you send it, then stop. Bend it to your client and your sales cycle, and tell the client in the proposal when you will check in.',
      },
      {
        q: 'How many times should I follow up on a proposal?',
        a: 'Three emails, then one calendar note to check in once about three months later with something new to say. If the client asks you to stop, stop that day.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your terms and your email practices checked by someone qualified where you work.',
      },
      {
        q: 'What subject line should a proposal follow-up use?',
        a: 'Something plain with the client’s business and the project, such as “[Client business name] website: your start date”. Skip “Following up” and “Just checking in”, which say nothing the client needs to answer.',
      },
      {
        q: 'Why ask the client to reply with a number?',
        a: 'A one-character reply is easier to send than a paragraph, so it is quicker for the client to answer. 1, 2 or 3 tells you whether to send the acceptance link, book a call or check back later.',
      },
      {
        q: 'What should I send when a client says no?',
        a: 'Thank them and ask one question, once: what would have made it a yes? Then close the proposal and do not ask again. The answer is often the most useful thing you learn from a lost proposal.',
      },
    ],
    relatedLinks: [
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Proposal follow-up email generator', path: '/tools/proposal-follow-up-generator' },
      { label: 'Client follow-up email guide', path: '/learn/client-follow-up-email' },
      { label: 'Web design revision policy template', path: '/templates/web-design-revision-policy' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
    ],
  },
  {
    slug: 'web-design-payment-reminder-emails',
    updatedOn: '2026-10-17',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design payment reminder emails template',
    cardCopy: 'Payment terms, a three-email reminder sequence, a phone-call script, subject lines and a tracker. PDF and Word.',
    h1: 'Free web design payment reminder emails template (PDF and Word).',
    metaTitle: 'Web Design Payment Reminder Emails: Free PDF & Word',
    description:
      'Download free web design payment reminder emails in PDF and Word: a gentle, firm and final-notice sequence, a phone-call script, subject lines and a tracker.',
    lede:
      'What to send when a web design client has not paid: terms agreed up front, then three short emails and one phone call, each with the invoice number, the amount, a date and a pay link.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-payment-reminder-emails-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design payment reminder emails template: a title and short intro, then "1. Payment terms" with invoice, late payment and pausing work wording, and the start of a table of the reminder sequence with heads-up, reminder 1 and reminder 2 rows.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-payment-reminder-emails-template.pdf', size: '141 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-payment-reminder-emails-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Payment terms', copy: 'Wording for when invoices are due, late payments and pausing work, to agree in writing before the first invoice.' },
      { title: 'The sequence at a glance', copy: 'A table of the heads-up, two reminders, a phone call and a final notice, with when each goes and what tone it takes.' },
      { title: 'Reminder 1: the gentle one', copy: 'The invoice number, amount, due date and a pay link, with a new pay-by date and no apology or threat.' },
      { title: 'Reminder 2: the firmer one', copy: 'The same facts, shorter, with how many days late it is and an offer to fix any problem with the invoice or the work.' },
      { title: 'The phone call', copy: 'A five-step script for the day you send reminder 2, ending with a written recap of what was agreed.' },
      { title: 'The final notice', copy: 'A plain email that says what happens next, and only what your agreement already says.' },
      { title: 'Subject lines and a tracker', copy: 'Four subject lines to skip and four to use instead, and a table to log every reminder, call and payment.' },
      { title: 'Checklist before you send', copy: 'Eight checks, from terms agreed up front to stopping the moment the invoice is paid.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional dental website on a $4,800 fixed fee. The $2,400 balance, invoice 1042, was due Friday the 6th. Reminders go out on the 9th and the 16th, with a call on the 16th, and the final notice on the 23rd gives until the 27th, which is 21 days after the due date.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Put the payment terms in your proposal or agreement before the first invoice goes out.',
      'Fill in the brackets and put the real dates for each reminder in your calendar when you send the invoice.',
      'Send the emails in order, make the call on the day of reminder 2, and stop the moment the invoice is paid.',
    ],
    product: {
      copy: 'Workspace369 workflow automations can send a reminder sequence by email, so each one goes out without you remembering. Email automations are on every plan. The same words go to every client, so read all three emails first and keep the phone call for yourself.',
      linkLabel: 'See workflow automations',
      path: '/features/automation',
    },
    faq: [
      {
        q: 'Is the web design payment reminder emails template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How many reminders should I send for an unpaid invoice?',
        a: 'The template uses three emails and one call: a gentle reminder the first business day after the due date, a firmer one about a week later with a call the same day, and a final notice about a week after that. The gaps are a suggestion, so pick yours.',
      },
      {
        q: 'What should a payment reminder email include?',
        a: 'The invoice number, the amount, the original due date, a pay link and a new pay-by date. Put the facts in the subject line too, so the number is in the client’s head before they open it.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Rules on late fees, interest and stopping work differ by country and state, so have your terms checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Web design invoice template', path: '/templates/web-design-invoice' },
      { label: 'Payment reminder generator', path: '/tools/payment-reminder-generator' },
      { label: 'Late payment follow-up sequence', path: '/blog/late-payment-follow-up-sequence' },
      { label: 'Late fee calculator', path: '/tools/late-fee-calculator' },
      { label: 'Net 30 payment terms', path: '/learn/net-30-payment-terms' },
    ],
  },
  {
    slug: 'web-design-price-sheet',
    updatedOn: '2026-10-17',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design price sheet template',
    cardCopy: 'A pricing worksheet for you, a three-package price sheet for the client, add-ons and two reply emails. PDF and Word.',
    h1: 'Free web design price sheet template (PDF and Word).',
    metaTitle: 'Web Design Price Sheet Template: Free PDF & Word',
    description:
      'Download a free web design price sheet in PDF and Word: a pricing worksheet, a three-package price sheet, an add-on list and price-question replies.',
    lede:
      'A method for turning “how much for a website?” into a price you can explain: work out the number privately, show the client three packages, and answer pushback by trimming scope, not your rate.',
    pages: 7,
    preview: {
      src: '/images/templates/web-design-price-sheet-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design price sheet template: a title and intro, a note that it gives a method and not a market rate, then "1. Your pricing worksheet" with an hourly rate line and a table of hours for discovery, design, build, content, changes and launch.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-price-sheet-template.pdf', size: '184 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-price-sheet-template.docx', size: '41 KB' },
    ],
    sections: [
      { title: 'Your pricing worksheet', copy: 'An internal page: your hourly rate, hours per stage, outside purchases with markup, fixed items and a suggested price. Never send it.' },
      { title: 'The three-package price sheet', copy: 'What the client sees: Starter, Standard and Plus with price, pages, rounds of changes, forms, launch support, what is not included and timeline.' },
      { title: 'Add-on list', copy: 'Prices for extra pages, extra rounds and other extras, set before the client asks.' },
      { title: 'The reply to “how much for a website?”', copy: 'A short email that names a package and a real price, says what is in it and offers two call times.' },
      { title: 'The reply to “that’s too expensive”', copy: 'Cut the scope, not the rate: a smaller version with its own price, worked out before you write.' },
      { title: 'Replies to two more pushbacks', copy: 'Wording for “can you do it for less?” and “why not hourly?”, with a phased option.' },
      { title: 'Checklist before you send', copy: 'Seven checks, from what is included and not to taxes, payment terms and the sums.' },
      { title: 'Acceptance', copy: 'A block to add under the price sheet: package chosen, a signature or typed name and the deposit that books the start date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional dental website at a made-up $90 an hour. The Standard package is 50 hours, so $4,500 plus $100 of photo licenses, a $20 markup and a $180 booking form comes to $4,800. Starter is $2,340 and Plus is $7,500. Every number is invented, not a market rate.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Work out your hourly rate and fill in the worksheet for each package. Keep it to yourself.',
      'Fill in the three-package price sheet and the add-on prices, and check that hours times rate plus extras equals each total.',
      'Send the price sheet with the reply email, and keep the “too expensive” reply ready.',
    ],
    product: {
      copy: 'Workspace369 keeps your services in a catalog and fills their price, unit and description into proposal lines. Optional add-on lines let the client pick extras themselves. You still set the prices.',
      linkLabel: 'See proposals in Workspace369',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the web design price sheet template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How much should I charge for a website?',
        a: 'The template gives a method, not a rate. Rates vary, so the example numbers are made up. Work out your own from your hours, your costs and the value to the client.',
      },
      {
        q: 'Should I show my hourly rate to the client?',
        a: 'No. The worksheet is internal. The client sees packages and totals, and when they ask for a lower price you discuss the scope, not your rate.',
      },
      {
        q: 'What do I say when a client says it is too expensive?',
        a: 'Offer a smaller scope with its own price, for example the same package without a booking form and two pages. Work out the smaller number first and check the sum before you send it.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information, not legal, tax or financial advice. Have your payment, deposit and tax wording checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Web design quote template', path: '/templates/web-design-quote' },
      { label: 'Rate increase email template', path: '/templates/web-design-rate-increase-email' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'How to price agency work', path: '/learn/how-to-price-agency-work' },
      { label: 'Web design pricing benchmarks', path: '/research/web-design-pricing-benchmarks' },
    ],
  },
  {
    slug: 'web-design-client-onboarding-checklist',
    updatedOn: '2026-10-17',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design client onboarding checklist template',
    cardCopy: 'A welcome email, access checklist, first-week plan, kickoff agenda, recap and a nudge for late content. PDF and Word.',
    h1: 'Free web design client onboarding checklist template (PDF and Word).',
    metaTitle: 'Web Design Client Onboarding Checklist: Free PDF & Word',
    description:
      'Download a free web design client onboarding checklist in PDF and Word: the welcome email, access checklist, first-week plan and kickoff call agenda.',
    lede:
      'The first week with a new web design client, planned: a welcome email with a job and a day for every line, access by invite instead of passwords, a short kickoff call and a recap that fixes the next date.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-client-onboarding-checklist-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design client onboarding checklist template: a title and intro, "1. The welcome email" with a subject line and three numbered jobs for the client, then the start of an access checklist table.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-client-onboarding-checklist-template.pdf', size: '138 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-client-onboarding-checklist-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'The welcome email', copy: 'Sent the day the deposit lands, with three jobs for the client: an invite instead of a password, a booking link and content due by a date.' },
      { title: 'Access checklist', copy: 'How the client gives you the website platform, domain, Google Analytics and Google Business Profile, each by invitation with a named role.' },
      { title: 'The first-week plan', copy: 'A day-by-day table from the deposit to the Friday recap, with who does what.' },
      { title: 'Kickoff call agenda', copy: 'A 30-minute agenda: what done looks like, who approves each round, what you still need, dates and questions.' },
      { title: 'The Friday recap email', copy: 'What is done, what is next and the client’s part, with a date.' },
      { title: 'The nudge for late content', copy: 'A short email that ties the date to something the client wants and says what moves if it slips, plus a reply for “I don’t have a logo”.' },
      { title: 'End-of-week-one checklist', copy: 'Seven checks that nothing is left to guess before week two.' },
      { title: 'Acceptance of the plan', copy: 'An optional block under your proposal’s signature: the client agrees to send items by the dates shown and that later dates may move the launch.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same first week for a fictional dental practice on a $4,800 fixed fee with a $2,400 deposit paid Friday the 6th. Invites go out on the 9th, the kickoff call is on the 10th, content is due the 12th and the recap on the 13th sets the design review for the 20th and launch for the 27th.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Send the welcome email the day the deposit lands, and ask for platform and domain invites on day one.',
      'Hold the 30-minute kickoff call and send the recap the same week with the next date.',
      'Go through the end-of-week checklist and send the nudge if content is late.',
    ],
    product: {
      copy: 'Workspace369 has intake forms for collecting what you need from a new client and project boards for tracking the first week.',
      linkLabel: 'See client onboarding in Workspace369',
      path: '/solutions/client-onboarding-software',
    },
    faq: [
      {
        q: 'Is the web design client onboarding checklist free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should I send a new web design client first?',
        a: 'A welcome email on the day the deposit lands with three jobs: invite you to their website platform without sharing a password, book the kickoff call, and send named content by a date.',
      },
      {
        q: 'How do I get access to a client’s website and domain?',
        a: 'Ask for an invitation by name and email with the role you need, on day one. Squarespace, WordPress.com, Google Analytics and Google Business Profile all use invitations, and each person needs their own account. Remove yourself at handover.',
      },
      {
        q: 'What do I do if the client’s content is late?',
        a: 'Send a short nudge the day after it was due. Say what you still need, a date, and what moves if it slips, such as the design review. Do not scold.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have anything about deadlines moving or what happens if the client is late checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Website project timeline template', path: '/templates/website-project-timeline' },
      { label: 'Web design discovery call questions template', path: '/templates/web-design-discovery-call-questions' },
      { label: 'Web design client questionnaire template', path: '/templates/web-design-client-questionnaire' },
      { label: 'Client onboarding checklist for agencies', path: '/blog/client-onboarding-checklist-for-agencies' },
      { label: 'Client onboarding questionnaire', path: '/learn/client-onboarding-questionnaire' },
    ],
  },
  {
    slug: 'web-design-discovery-call-questions',
    updatedOn: '2026-10-17',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design discovery call questions template',
    cardCopy: 'A booking email, short form, opening script, seven questions, notes sheet, budget replies and follow-up. PDF and Word.',
    h1: 'Free web design discovery call questions template (PDF and Word).',
    metaTitle: 'Web Design Discovery Call Questions: Free PDF & Word',
    description:
      'Download free web design discovery call questions in PDF and Word: the booking email, a short form, an opening script, seven questions and a notes sheet.',
    lede:
      'Everything for the call before you quote: a short form so the call is for the “why”, seven questions with follow-ups, a notes sheet that feeds your quote and a follow-up email with a day on it.',
    pages: 6,
    preview: {
      src: '/images/templates/web-design-discovery-call-questions-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design discovery call questions template: a title and intro, "1. The booking email" with a subject line and a short message about the call length and a fixed quote, then "2. The short form to send ahead" with five numbered questions.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-discovery-call-questions-template.pdf', size: '155 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-discovery-call-questions-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'The booking email', copy: 'Sent when they say yes to a call: the length, the goal of leaving with what you need for a fixed quote, and a link to the short form.' },
      { title: 'The short form to send ahead', copy: 'Five questions on the business, the goal, three liked sites, who has the words and photos, and a live-by date.' },
      { title: 'The opening script', copy: 'Say the time and the goal first, and ask before recording a call.' },
      { title: 'The seven questions', copy: 'Goal, success, what is not working, pages and features, content, who signs off, and range and date, each with a follow-up.' },
      { title: 'Notes sheet', copy: 'Fill in live: their words on one side, what it means for the quote on the other. Every line needs a goal, a number or a day, and an owner.' },
      { title: 'The budget question', copy: 'How to ask for a range without asking cold, and replies for “I have no idea” and “I won’t say”.' },
      { title: 'The follow-up email', copy: 'Sent within a few hours: what you heard, the day the quote lands, one reply to confirm and a walkthrough you booked before hanging up.' },
      { title: 'Ready to quote? Checklist', copy: 'Eight checks, including that you have not named a price on the call.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional dental website call lasting 25 minutes with a practice manager. The notes sheet captures a goal of more online bookings and a new patient worth about $800 a year. Three sizes are offered, $7,500, $4,800 and $2,340, and the follow-up promises a quote by Friday the 6th.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Send the booking email with the short form, and have the notes sheet open during the call.',
      'Ask the seven questions in their order, use the budget wording and book a walkthrough before you hang up.',
      'Send the follow-up email within a few hours and check the ready-to-quote list before you price.',
    ],
    product: {
      copy: 'In Workspace369 a prospect can fill your intake form and land in your Requests inbox as a request. One click converts it to a client.',
      linkLabel: 'See intake forms in Workspace369',
      path: '/solutions/client-intake-form-software',
    },
    faq: [
      {
        q: 'Is the web design discovery call questions template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should I ask on a web design discovery call?',
        a: 'What the site should do, what success looks like, what is not working now, which pages and features they picture, who writes the words and sends photos and by when, who signs off, and whether they have a range and a date in mind.',
      },
      {
        q: 'How long should a discovery call be?',
        a: 'The template suggests 25 minutes. There is no standard length, so pick what you can hold and say it up front.',
      },
      {
        q: 'How do I ask a client about their budget?',
        a: 'Say why you are asking: “To suggest the right size of site, do you have a range in mind?” If they have no idea, offer three sizes, biggest first. If they will not say, offer two options and ask which is closer.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your terms checked by someone qualified where you work, and check your local rules before recording any call.',
      },
    ],
    relatedLinks: [
      { label: 'Web design client onboarding checklist template', path: '/templates/web-design-client-onboarding-checklist' },
      { label: 'Web design client questionnaire template', path: '/templates/web-design-client-questionnaire' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Client intake checklist', path: '/learn/client-intake-checklist' },
      { label: 'Creative brief template', path: '/learn/creative-brief-template' },
    ],
  },
  {
    slug: 'web-design-contract',
    updatedOn: '2026-10-19',
    proposalVsContract: true,
    cardTitle: 'Web design contract template',
    cardCopy: 'Eight short sections for a small website project: deliverables, ownership, fees, changes and cancelling. PDF and Word.',
    h1: 'Free web design contract template (PDF and Word).',
    metaTitle: 'Web Design Contract Template: Free PDF & Word | Workspace369',
    description:
      'Download a free web design contract template in PDF and Word: deliverables, ownership, fees, payment dates, changes and acceptance. No email needed.',
    lede:
      'A short agreement for a small business website, in plain words: what you will deliver, who owns the result, when payments are due and what happens if the project stops. Fill in the brackets, have it checked and send it with your proposal.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-contract-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design contract template: a "Website Design Agreement: [Client business name]" header with client, designer, date and project lines, then the "The parties and the project" and "What I will deliver" sections with a bulleted list of pages, platform and revision rounds.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-contract-template.pdf', size: '126 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-contract-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'The parties and the project', copy: 'One short paragraph: who is agreeing to what, with the client, the designer, the date and a short project name.' },
      { title: 'What I will deliver', copy: 'The pages, the platform and the revision rounds, so “a website” means something you can point to. Anything else is a change request.' },
      { title: 'Who owns what', copy: 'The final site, your client’s own content, third-party fonts and plugins, working files and portfolio use, decided in writing before you start.' },
      { title: 'Fees and payment', copy: 'The project fee, a payment to start, a payment before launch, due dates, a work-on-hold line for late payment and who pays for hosting and plugins.' },
      { title: 'What I need from you', copy: 'Content by a date, one person who approves, feedback within a set number of days, and access under your own login.' },
      { title: 'Changes', copy: 'Anything outside the deliverables gets a short written price and the time it takes. Work starts once the client approves it.' },
      { title: 'Cancelling', copy: 'Notice period, payment for work completed and who keeps the finished and unfinished pages if the project stops halfway.' },
      { title: 'Acceptance', copy: 'Client and designer names, signatures, dates and the day the first invoice goes out.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same agreement for a fictional dental practice: a five-page Squarespace site, two revision rounds and a $4,800 fee paid as $2,400 to start and $2,400 before launch, with ownership passing on the final payment. Use it to see what “specific” looks like; your numbers will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Replace every [bracket] with your project’s details and delete the grey guidance lines.',
      'Choose your ownership and cancellation options, and match the deliverables to your proposal.',
      'Have the terms checked where you work, send it with the proposal and start once it is signed.',
    ],
    product: {
      copy: 'In Workspace369, you can send these terms as a contract that clients review and sign online, on every plan. Clients accept the proposal through a link and can pay a deposit on acceptance.',
      linkLabel: 'See Workspace369 Contracts',
      path: '/features/contracts',
    },
    faq: [
      {
        q: 'Is the web design contract template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information for a small project, not a full legal contract. Have your terms checked by someone qualified where you work, especially ownership, payment, liability and late-fee wording. It has no liability or indemnity terms, so have those added.',
      },
      {
        q: 'Who should own the finished website?',
        a: 'The template passes ownership to the client when the final invoice is paid in full, and has a licence option if you would rather keep ownership. Decide it in writing before you start, and have it checked where you work.',
      },
      {
        q: 'Do I still need a proposal?',
        a: 'Yes. The contract can point to your proposal by date for the scope of work and the revision rules, so the two documents agree. The proposal sells the project and the contract records the terms.',
      },
    ],
    relatedLinks: [
      { label: 'Client offboarding email template', path: '/templates/web-design-client-offboarding-email' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Web design contract template guide', path: '/learn/web-design-contract-template' },
      { label: 'Freelance contract template', path: '/tools/freelance-contract-template' },
      { label: 'Website scope of work template', path: '/templates/website-scope-of-work' },
    ],
  },
  {
    slug: 'website-handover-checklist',
    updatedOn: '2026-10-17',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Website handover checklist template',
    cardCopy: 'A handover checklist and document: what is handed over, who owns each account, logins, backup and support. PDF and Word.',
    h1: 'Free website handover checklist template (PDF and Word).',
    metaTitle: 'Website Handover Checklist: Free PDF & Word | Workspace369',
    description:
      'Download a free website handover checklist in PDF and Word: what is handed over, account ownership, logins, backup and sign-off. No email needed.',
    lede:
      'When a site goes live, the client should know what they are getting and who owns each account. This checklist and document lists the files, the logins, the backup and how long you will fix problems at no charge.',
    pages: 5,
    preview: {
      src: '/images/templates/website-handover-checklist-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the website handover checklist template: a "Website Handover for [Client business name]" header with prepared for, prepared by, handover date and site lines, then "The site and this handover" and a "What is handed over" list of tick-box items such as the live site, design files and brand assets.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/website-handover-checklist-template.pdf', size: '130 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/website-handover-checklist-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'The site and this handover', copy: 'One short paragraph: what is being handed over and the date it takes effect.' },
      { title: 'What is handed over', copy: 'A tick list of the live site, design files, brand assets, final images and text, a backup and the list of plugins and integrations.' },
      { title: 'Who owns what', copy: 'A table of the domain, hosting or platform plan, the site, analytics and search accounts, with who owns each, who pays and what access you keep.' },
      { title: 'Logins and access', copy: 'Invite people with their own email instead of sending passwords, and ask the client to log in to each account and reply “received”.' },
      { title: 'Backup and files', copy: 'The date and location of the backup, how to restore it and how long you keep your own copies.' },
      { title: 'How to edit the site', copy: 'A short walkthrough, what the client can safely edit and what is better left to you.' },
      { title: 'Support after launch', copy: 'A free-fix window with an end date, how to ask for a fix and what is not covered once it closes.' },
      { title: 'Sign-off', copy: 'A signature or a typed “Received and accepted” once the client has logged in to each account and received each item.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same handover for a fictional bakery on Squarespace: handed over on November 16, a backup made on November 15, a 30-minute walkthrough on November 17 and free fixes until November 30. Use it to see what “specific” looks like; your dates will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Replace every [bracket] with your project’s details, delete what you did not build and make a backup before launch.',
      'Invite the client to each account with their own email, and make them the owner of the domain and the site.',
      'Send it on handover day, ask for a “received” reply once they have logged in, and set the free-fix end date.',
    ],
    product: {
      copy: 'Handing over several sites? In Workspace369 you can keep the checklist as a project with task cards on a board.',
      linkLabel: 'See freelance project management',
      path: '/solutions/freelance-project-management-software',
    },
    faq: [
      {
        q: 'Is the website handover checklist free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should a website handover include?',
        a: 'The live site, design files, brand assets, final images and text, a backup, and a list of the plugins and integrations. It should also say who owns the domain, hosting and analytics, how the client logs in, and how long you fix problems at no charge.',
      },
      {
        q: 'Should I send my client their passwords?',
        a: 'No. Invite them to each account with their own email and ask them to accept the invite. A shared document full of passwords is one you cannot take back, and you should use your own login rather than theirs.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Who owns the design, code and content is set by your agreement, not by this document. Have your terms checked by someone qualified where you work, especially ownership, liability, support and payment wording.',
      },
    ],
    relatedLinks: [
      { label: 'Client offboarding email template', path: '/templates/web-design-client-offboarding-email' },
      { label: 'Website maintenance agreement template', path: '/templates/website-maintenance-agreement' },
      { label: 'Web design client onboarding checklist template', path: '/templates/web-design-client-onboarding-checklist' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Testimonial request email template', path: '/templates/testimonial-request-email' },
    ],
  },
  {
    slug: 'web-design-invoice',
    updatedOn: '2026-10-17',
    cardTitle: 'Web design invoice template',
    cardCopy: 'Invoice details, line items, deposit and balance, a real due date, the invoice email and late payment wording. PDF and Word.',
    h1: 'Free web design invoice template (PDF and Word).',
    metaTitle: 'Web Design Invoice Template: Free PDF & Word | Workspace369',
    description:
      'Download a free web design invoice template in PDF and Word: line items, deposit and balance, a due date, the invoice email and late wording. No email needed.',
    lede:
      'An invoice your client can pay without a question: one clear line for the site, the deposit already paid, the balance, and a real due date with a link to pay. Fill in the brackets and send it with the email.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-invoice-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design invoice template: a "Web Design Invoice Template" title and intro, then "Invoice details" as a table of fields such as invoice number, date, supply date and client address, and the start of a "Line items" table with a description and amount column.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-invoice-template.pdf', size: '133 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-invoice-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'Invoice details', copy: 'A table of the invoice number, date, supply date, both sides’ names and addresses and a tax or VAT number if you are registered.' },
      { title: 'Line items', copy: 'One clear line for the site and its pages, optional add-ons, a subtotal, tax on its own line and the total.' },
      { title: 'Deposit and balance', copy: 'The deposit already paid as its own line, so the balance is no surprise: total minus deposit equals balance due.' },
      { title: 'Payment terms and due date', copy: 'The day the invoice is due, not just “Net 15”, set before launch, with a pay link or bank details.' },
      { title: 'The invoice email', copy: 'A short email that states the amount, the day and the link to pay.' },
      { title: 'Extra-work invoice', copy: 'A separate invoice for a change, with hours times rate on one line and its own number.' },
      { title: 'Late payment wording', copy: 'Two options, no late fee or a fee checked locally, and a plain reminder for the day after the due date.' },
      { title: 'Checklist before you send', copy: 'Eight checks, from the unique number to the pay link, so nothing is missing when the invoice goes out.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional dental practice’s seven-page site at a fixed $4,800: a $2,400 deposit paid on October 9, a balance of $2,400 due Friday, November 6, and a separate $270 invoice for a third revision round. Use it to see what “specific” looks like; your numbers will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Fill in the invoice details, name the site and its pages on one line, and show the deposit already paid.',
      'Pick a weekday for the due date, add your pay link, and have the late-fee wording checked where you work.',
      'Send it with the invoice email, and send the reminder on the day after the due date if it is unpaid.',
    ],
    product: {
      copy: 'In Workspace369, invoices include a payment link and update their status when paid. An accepted proposal can convert to an invoice with its line items, total and deposit.',
      linkLabel: 'See Workspace369 Invoices',
      path: '/features/invoices',
    },
    faq: [
      {
        q: 'Is the web design invoice template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should a web design invoice include?',
        a: 'A unique invoice number and date, both sides’ names and addresses, one clear line naming the site and its pages, the deposit already paid, the balance, tax on its own line if you charge it, and a due date with a way to pay.',
      },
      {
        q: 'Should I write “Net 15” or an actual date?',
        a: 'Write the day. “Net 15” makes the client count from the issue date, and a real weekday is easier to pay and to chase. The template sets the balance due before launch: paid, then live.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information, not legal or tax advice. Have your terms and invoice fields checked by someone qualified where you work, especially tax, late-fee and refund wording.',
      },
    ],
    relatedLinks: [
      { label: 'Web design payment reminder emails template', path: '/templates/web-design-payment-reminder-emails' },
      { label: 'How to write an invoice', path: '/learn/how-to-write-an-invoice' },
      { label: 'Free invoice template', path: '/tools/invoice-template' },
      { label: 'Invoice due date calculator', path: '/tools/invoice-due-date-calculator' },
      { label: 'Web design deposit request template', path: '/templates/web-design-deposit-request' },
    ],
  },
  {
    slug: 'testimonial-request-email',
    updatedOn: '2026-10-17',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Testimonial request email template',
    cardCopy: 'A request email, a short Google review message, three questions, a reminder and replies to pushbacks. PDF and Word.',
    h1: 'Free testimonial request email template (PDF and Word).',
    metaTitle: 'Testimonial Request Email: Free PDF & Word | Workspace369',
    description:
      'Download a free testimonial request email in PDF and Word: three rules for asking, the request email, a Google review message, one reminder and an example.',
    lede:
      'Ask every web design client for honest words once the site is live. This template has the email, a short Google review message and three questions that get a specific answer, with no rewards and no scripts.',
    pages: 5,
    preview: {
      src: '/images/templates/testimonial-request-email-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the testimonial request email template: a "Testimonial Request Email Template" title and intro, then "Before you send: three rules" as three bullets on asking every client, offering no reward and not scripting it, and the start of "The request email" with a subject line and short body.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/testimonial-request-email-template.pdf', size: '96 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/testimonial-request-email-template.docx', size: '7 KB' },
    ],
    sections: [
      { title: 'Before you send: three rules', copy: 'Ask every client, offer no reward and do not script the answer, based on Google’s Maps policy and the FTC’s published Q&A.' },
      { title: 'The request email', copy: 'A short email sent within a week of launch that names the time it takes, has the link inside and asks for honest words, not stars.' },
      { title: 'The short Google review message', copy: 'A two-line text or chat message with your Google review link.' },
      { title: 'The three testimonial questions', copy: 'What the problem was, what you did about it and what changed since, so the answer is specific.' },
      { title: 'The reminder', copy: 'One nudge about five days later, then let it go.' },
      { title: 'The use-my-name note', copy: 'Ask before you publish a client’s words, name, photo or logo, and keep their reply.' },
      { title: 'Replies to two pushbacks', copy: 'What to say to “I’m too busy right now” and “I didn’t love one part”.' },
      { title: 'Checklist before you send', copy: 'Six checks, from asking every client to the plan for one reminder, and a note to add the request to your launch checklist.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio sends the same request to all six clients who launched this quarter, two weeks after the Harbor Dental site went live, with a made-up three-line answer to the three questions. Use it to see what “specific” looks like; your words will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Add your Google review link or a testimonial form link, and delete the grey guidance lines.',
      'Send the request email to every client within a week of launch, with no discount or gift attached.',
      'Send one reminder about five days later, then stop, and ask before you publish a name, photo or logo.',
    ],
    product: {
      copy: 'In Workspace369, an email workflow can start from task activity in your projects and send the email for you. It goes to every client, not just the happy ones.',
      linkLabel: 'See Workflow Automations',
      path: '/features/automation',
    },
    faq: [
      {
        q: 'Is the testimonial request email template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'Can I offer a discount for a review?',
        a: 'No. Google’s policy says merchants can’t offer incentives such as payment, discounts or free goods in exchange for a review. Say thank you and stop there.',
      },
      {
        q: 'Should I only ask my happiest clients?',
        a: 'No. Google’s policy says not to selectively solicit positive reviews, and the FTC’s Q&A says asking only customers you think are happy could violate the FTC Act. Ask every client from the period.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Check the rules where you work, especially around incentives, review requests and publishing clients’ names.',
      },
    ],
    relatedLinks: [
      { label: 'Referral request email template', path: '/templates/referral-request-email' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
      { label: 'Web design client onboarding checklist template', path: '/templates/web-design-client-onboarding-checklist' },
      { label: 'Web design proposal follow-up email template', path: '/templates/web-design-proposal-follow-up-emails' },
      { label: 'Client onboarding automation', path: '/blog/client-onboarding-automation' },
    ],
  },
  {
    slug: 'web-design-quote',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design quote template',
    cardCopy: 'A quote-or-proposal check, a one-page fixed-price quote, an estimate version, the reply email and the “yes” confirmation. PDF and Word.',
    h1: 'Free web design quote template (PDF and Word).',
    metaTitle: 'Web Design Quote Template: Free PDF & Word | Workspace369',
    description:
      'Download a free web design quote in PDF and Word: a quote-or-proposal check, a one-page fixed-price quote, an estimate version and the reply email.',
    lede:
      'For a small job and a client who already trusts you: one price, one page, one clear way to say yes. This template also tells you when the job has outgrown a quote and needs a proposal instead.',
    pages: 6,
    preview: {
      src: '/images/templates/web-design-quote-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design quote template: a "Web Design Quote Template" title and intro, a note that it gives a format and not a market rate, the "Quote or proposal?" check as three checkbox lines, and the start of "The one-page quote".',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-quote-template.pdf', size: '131 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-quote-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Quote or proposal?', copy: 'A three-point check for you: the job fits in two lines, there is one price and the client already trusts you. If not, send a proposal.' },
      { title: 'The one-page quote', copy: 'Quote number, date, the job in two lines, priced lines with one fixed total, what is not included and the price of a second round, payment, taxes, a valid-until date and how to accept.' },
      { title: 'The estimate version', copy: 'For when you can’t promise the price yet: a range or “to be confirmed”, said at the top, and what will settle it.' },
      { title: 'The reply email', copy: 'A short email with the lines added up, what is not included, the payment split and a valid-until date.' },
      { title: 'When they say yes', copy: 'A confirmation that books the client in and asks for the first payment, sent once you have the written yes, plus a one-line reminder two days before the valid-until date.' },
      { title: 'Checklist before you send', copy: 'Eight checks, from whether a quote is right for the job and “fixed price” or “estimate” at the top to the one-line reminder, payment terms, taxes and having your terms checked.' },
      { title: 'Acceptance', copy: 'A block to add under the quote if you use a link or a form: how to accept, the first payment and the quote number, client name and date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio quotes one new page for an existing dental client: two lines that add up to a fixed total, half to start and half when it is live, valid for 14 days. It also shows the estimate version, the “yes” reply and the reminder. Every number is invented, not a market rate.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Run the quote-or-proposal check. If all three are true, fill in the one-page quote and make sure the lines add up to the total.',
      'Send it with the reply email, name what is not included and put a valid-until date on it.',
      'Wait for the written “yes” before you start, then send the confirmation with the first payment link. No reply? Send the one-line reminder two days before the valid-until date.',
    ],
    product: {
      copy: 'In Workspace369, a quote can go out as a short proposal with priced lines, a valid-until date and the deposit taken when the client accepts. Building proposals is on every plan.',
      linkLabel: 'See quoting in Workspace369',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the web design quote template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'When should I send a quote and when a proposal?',
        a: 'Send a quote when you can describe the job in two lines, there is one price and the client already trusts you. Send a proposal if the client is new, there are options to weigh or the job is not pinned down. This is a rule of thumb, not a standard.',
      },
      {
        q: 'What if I can’t promise the price yet?',
        a: 'Use the estimate version. Say at the top that it is an estimate, not a fixed price, give a range or “to be confirmed” and say what will settle it, such as a short call, by a set date.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Rules on quotes, estimates and deposits differ by country and state, so have your terms checked by someone qualified where you work, especially payment, deposit and tax wording.',
      },
    ],
    relatedLinks: [
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Web design price sheet template', path: '/templates/web-design-price-sheet' },
      { label: 'Web design deposit request template', path: '/templates/web-design-deposit-request' },
      { label: 'Free estimate template', path: '/tools/estimate-template' },
      { label: 'Website scope of work template', path: '/templates/website-scope-of-work' },
    ],
  },
  {
    slug: 'difficult-client-reply-scripts',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Difficult client reply scripts',
    cardCopy: 'The first reply, call questions, a written recap and replies to a rude message, a late-night text and “you’re late”. PDF and Word.',
    h1: 'Free difficult client reply scripts (PDF and Word).',
    metaTitle: 'Difficult Client Reply Scripts: Free PDF & Word',
    description:
      'Download free difficult client reply scripts in PDF and Word: replies to “this isn’t what I asked for”, a rude message, a late-night text and a late client.',
    lede:
      'When a project turns tense, the first reply sets the tone. These scripts help you answer calmly, keep the facts in writing and leave a record you can point to.',
    pages: 5,
    preview: {
      src: '/images/templates/difficult-client-reply-scripts-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the difficult client reply scripts template: a title and intro, "Before you reply: three rules" as three bullets, and "The first reply to This isn’t what I asked for" with a short email and a before-you-send check.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/difficult-client-reply-scripts-template.pdf', size: '103 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/difficult-client-reply-scripts-template.docx', size: '8 KB' },
    ],
    sections: [
      { title: 'Before you reply: three rules', copy: 'Wait an hour and send it the same day, read it out loud and cut every “actually”, and reply in the same thread.' },
      { title: 'The first reply to “This isn’t what I asked for”', copy: 'Thank them, point at the version they approved in writing and offer a 15-minute call with two times.' },
      { title: 'The call: three questions and what to say', copy: 'What feels off, what they expected instead and what would put it right. Decide nothing on the call and confirm in writing.' },
      { title: 'The recap email', copy: 'What you heard, what happens next and a line that lets them correct you, sent within the hour.' },
      { title: 'If it’s your mistake', copy: 'One line, no long explanation: you’re right, my mistake, here is when the fix arrives.' },
      { title: 'Reply to a rude message', copy: 'Reply to the work, not the tone: one calm limit, one question and a call.' },
      { title: 'Reply to the late-night message', copy: 'Answer in the morning and name a time, then put the rule in your welcome email.' },
      { title: 'Reply to “you’re late” when the delay is theirs', copy: 'State dates, not blame: launch moves one day for each day the client is late.' },
      { title: 'When it keeps happening', copy: 'Keep a short log, set expectations before you start and read what your agreement says about ending early.' },
      { title: 'Checklist before you send', copy: 'Six checks, from waiting an hour to not offering a discount just to end the conversation.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio gets “This isn’t what I asked for” on day 12 of a dental website. The example runs through the first reply, the call, the recap, a mistake owned in one line, a late-night text and a “you’re late” answer where the dates add up.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Wait an hour, then fill in the first reply and offer two call times. Only point at a version the client approved in writing.',
      'On the call, ask the three questions and decide nothing. Send the recap email within the hour.',
      'Sort what you heard: fix your mistakes in one line, treat new ideas as change requests and ask which part for vague feelings.',
    ],
    product: {
      copy: 'In Workspace369, projects and task boards are on every plan, and clients can watch progress in a client portal instead of asking for updates.',
      linkLabel: 'See the client portal',
      path: '/features/client-portal',
    },
    faq: [
      {
        q: 'Is the difficult client reply scripts template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How should I answer “This isn’t what I asked for”?',
        a: 'Thank them for telling you, point at the version they approved in writing (only if they did), and offer a 15-minute call with two times. Don’t write “sorry you feel that way” and don’t carry the whole conversation by email.',
      },
      {
        q: 'What if the client says I’m late and the delay is theirs?',
        a: 'State dates, not blame. Say what was due, when it arrived and that launch moves one day for each day they were late. AIGA says most design firms specify a day-for-day extension, so check that your agreement says so.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. What your agreement lets you do about changes, delays or ending a project differs by country and state, so have your terms checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Say no to a client: reply templates', path: '/templates/say-no-to-a-client-replies' },
      { label: 'Web design revision policy template', path: '/templates/web-design-revision-policy' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'How to fire a client', path: '/learn/how-to-fire-a-client' },
      { label: 'Client communication best practices', path: '/learn/client-communication-best-practices' },
    ],
  },
  {
    slug: 'web-design-client-offboarding-email',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Client offboarding email template',
    cardCopy: 'An exit email, a quiet-client version, three push-back replies, a handover list and final invoice wording. PDF and Word.',
    h1: 'Free web design client offboarding email template (PDF and Word).',
    metaTitle: 'Client Offboarding Email Template: Free PDF & Word',
    description:
      'Download a free web design client offboarding email in PDF and Word: four checks, the exit email, final invoice wording, push-back replies and a list.',
    lede:
      'Ending a client relationship goes better with one honest sentence, two dates and a number. This template gives you the exit email, the final invoice and the handover, without blame.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-client-offboarding-email-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the client offboarding email template: a title and intro, "Before you send: four checks" as four bullets (the problem in writing, underpriced, handover, your agreement), and the start of "The exit email".',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-client-offboarding-email-template.pdf', size: '106 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-client-offboarding-email-template.docx', size: '8 KB' },
    ],
    sections: [
      { title: 'Before you send: four checks', copy: 'Have you named the problem once in writing, is this client underpriced, can you hand over what the client has paid for, and what does your agreement say about ending early?' },
      { title: 'The exit email', copy: 'One honest sentence about fit, your last day, the handover day and the final invoice number, amount, due date and link.' },
      { title: 'The final invoice wording', copy: 'A worked example of hours worked less the deposit already paid, with a line for the invoice.' },
      { title: 'The quiet-client version', copy: 'The same email with one change: a date to reply by, then the project closes.' },
      { title: 'Three replies for push-back', copy: 'Short answers to “Please stay”, “At least finish one more thing” and “I’m angry”.' },
      { title: 'The handover list', copy: 'Where things stand, design files and exports, the client’s own logo, photos and text, logins and access, and what is still owed.' },
      { title: 'Checklist before you send', copy: 'Seven checks, from naming the problem in writing to reading your agreement’s section on ending early and replying once to push-back.' },
      { title: 'Sending the final invoice', copy: 'A note on putting the payment link and the due date in the email itself.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio stops a dental website after 31 of 50 hours, having named the problem in writing first. It sends the exit email on a Monday, works out a final invoice of hours worked less the deposit, answers “Please stay” and sends a handover note. Every number is invented.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Run the four checks, including whether the client is underpriced, and read your agreement’s section on ending early before you write anything.',
      'Fill in the exit email with your last day, the handover day and the final invoice, and put the pay link in the email, not an attachment.',
      'Keep doing the promised work until your last day, reply once to push-back, then send the handover list.',
    ],
    product: {
      copy: 'In Workspace369, invoices include a payment link and update their status automatically when paid. Payment links are on every plan.',
      linkLabel: 'See Workspace369 Invoices',
      path: '/features/invoices',
    },
    faq: [
      {
        q: 'Is the web design client offboarding email template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should I check before ending a client relationship?',
        a: 'Check that you have named the problem once in writing, whether this client is underpriced (a higher rate on the next job is worth trying first), that you can hand over what the client has paid for, and what your agreement says about ending early. If the problem is an unpaid invoice, send the payment reminders first.',
      },
      {
        q: 'What if the client has gone quiet?',
        a: 'Send the same email with a date to reply by. If you hear back by then, you can pick the work up. If not, you close the project and send the files and final invoice on the date you gave.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. What your agreement says about ending early, unfinished work and handing over files differs by contract and by place, so have your terms checked where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Client ghosting email sequence', path: '/templates/client-ghosting-email-sequence' },
      { label: 'Web design contract template', path: '/templates/web-design-contract' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
      { label: 'Testimonial request email template', path: '/templates/testimonial-request-email' },
      { label: 'Website maintenance agreement template', path: '/templates/website-maintenance-agreement' },
    ],
  },
  {
    slug: 'web-design-rate-increase-email',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Rate increase email template',
    cardCopy: 'A worksheet in each client’s dollars, the notice email, a send-by schedule, three push-back replies and an agreement clause. PDF and Word.',
    h1: 'Free web design rate increase email template (PDF and Word).',
    metaTitle: 'Rate Increase Email Template: Free PDF & Word | Workspace369',
    description:
      'Download a free web design rate increase email in PDF and Word: a worksheet for each client, the notice, a send-by schedule, push-back replies and a clause.',
    lede:
      'A method for raising your rate with the clients you already have: work out what it means in their dollars, say what stays the same and give clear notice. It gives you a method, not a market rate.',
    pages: 5,
    preview: {
      src: '/images/templates/web-design-rate-increase-email-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the rate increase email template: a title and intro, "Your worksheet" with old and new rate lines, a table of hours per client, a table for an extra round and an added page, and the "What stays at the old rate" note.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-rate-increase-email-template.pdf', size: '143 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-rate-increase-email-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Your worksheet', copy: 'An internal page: old and new rate, each hourly client’s monthly and yearly difference, extra rounds and added pages, what stays at the old rate and your side of the sum: the billed hours you can lose and earn the same. Never send it.' },
      { title: 'The notice', copy: 'Old rate, new rate and start date in the first line, what it means in the client’s dollars, what stays the same and a thank you.' },
      { title: 'When to send it', copy: 'A send-by schedule from deciding the rate to the day it starts, with notice of about two months and a short call for your biggest clients.' },
      { title: 'Three replies for push-back', copy: 'Answers to “That’s a big jump”, “Does this apply to my current project?” and “We’ll look elsewhere”.' },
      { title: 'A line for your agreement', copy: 'An example clause on standard rates and written notice, to have checked where you work.' },
      { title: 'The day it starts (checklist)', copy: 'Eight checks, from the notice going to every client to your price sheet, proposal template and saved invoice items showing the new rate, and the notice clause in your agreement.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio raises its hourly rate and writes to a dental client who buys about 4 hours of updates a month. The example shows the monthly and yearly difference, what stays at the old rate, your side of the sum (six billed hours at the new rate earn what seven did at the old one) and the reply to “That’s a big jump”. Every number is invented.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Fill in the worksheet for each hourly client, work out your side of the sum and decide what stays at the old rate before you write.',
      'Send the notice to every existing client in the same week, from your own address, about two months ahead.',
      'Hold the date, answer push-back with the three replies and update your price sheet and proposal template on the day it starts.',
    ],
    product: {
      copy: 'In Workspace369, your services sit in a catalog, and an item’s price fills into proposals and invoices when you add it. You send the notice email yourself, from your own inbox.',
      linkLabel: 'See the product catalog',
      path: '/features/products',
    },
    faq: [
      {
        q: 'Is the web design rate increase email template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How much notice should I give before a rate increase?',
        a: 'Check your current agreement first. AIGA’s form gives 30 days’ advance notice as an example clause, not a legal notice period. The template suggests giving more, about two months, so nobody learns about the change from an invoice.',
      },
      {
        q: 'Should the new rate apply to work we already agreed?',
        a: 'Decide this before you write. The template suggests that anything the client has already agreed to, such as an accepted quote or a fixed-price project in progress, keeps the price they said yes to, and the notice says so.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Notice rules and what your agreement allows differ by country and state, so check your current agreement and have your terms checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Web design price sheet template', path: '/templates/web-design-price-sheet' },
      { label: 'Web design pricing benchmarks', path: '/research/web-design-pricing-benchmarks' },
      { label: 'Hourly rate calculator', path: '/tools/hourly-rate-calculator' },
      { label: 'Website maintenance agreement template', path: '/templates/website-maintenance-agreement' },
      { label: 'Web design contract template', path: '/templates/web-design-contract' },
    ],
  },
  {
    slug: 'say-no-to-a-client-replies',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Say no to a client: reply templates',
    cardCopy: 'Four replies for a rush date, a discount, a “can you just” favor and a job you can’t build, plus a pushback reply. PDF and Word.',
    h1: 'Free say no to a client reply templates (PDF and Word).',
    metaTitle: 'Say No to a Client Reply Templates: Free PDF & Word',
    description:
      'Download free reply templates for saying no to a web design client in PDF and Word: the No, Yes, Next formula and replies to a rush date or a discount.',
    lede:
      'For the requests that are not revisions or scope changes. Each reply gives a plain no, one real yes and a next step. Most end on a one-word reply the client can send, so you can decline without a long back-and-forth.',
    pages: 5,
    preview: {
      src: '/images/templates/say-no-to-a-client-replies-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the say no to a client reply templates: a "Say No to a Client: Reply Templates" title and intro, then "The formula: No, Yes, Next" as three bullets, and the start of "Reply: a rush date" with a subject line and short reply.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/say-no-to-a-client-replies-template.pdf', size: '96 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/say-no-to-a-client-replies-template.docx', size: '7 KB' },
    ],
    sections: [
      { title: 'The formula: No, Yes, Next', copy: 'Say the no in the first line, offer one real yes you will keep and end on a step the client can take with a single word.' },
      { title: 'Reply: a rush date', copy: 'A reply that offers a phased launch when the client wants the date moved up, with a note to check your other clients’ deadlines first, based on AIGA’s Standard Form of Agreement.' },
      { title: 'Reply: a discount', copy: 'Cut the scope, not the rate, with a check of the sum, and a separate reply for a friend who asks for a “friend price”.' },
      { title: 'Reply: a “can you just” favor', copy: 'A reply for work outside the website, such as setting up email, with a guide or a short paid session as the yes.' },
      { title: 'Reply: a job you can’t build', copy: 'Name one specialist you would hire yourself and let the client hire them directly.' },
      { title: 'If the client pushes back', copy: 'Say the yes again and the next step, without adding reasons.' },
      { title: 'Phrases to cut', copy: 'Five lines that weaken a no, such as “Just this once.”' },
      { title: 'Checklist before you send', copy: 'Six checks, from putting the no in the first line to checking your other clients’ dates, and a note on keeping the dates straight.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio answers a seven-page, $4,800 site client with all four replies and a pushback, with made-up numbers that add up. Use it to see how short a no can be; your words will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Pick the reply that matches the request and fill in every bracket, then delete the guidance in italics.',
      'Check your other clients’ dates and the sums before you offer a date or a price.',
      'End on the next step, a one-word reply where you can, and if the client pushes back, say the yes again instead of adding reasons.',
    ],
    product: {
      copy: 'In Workspace369, each project shows its progress and deadline, with its tasks on a board. Check it before you answer a rush request. It does not write or send a no for you.',
      linkLabel: 'See agency project management',
      path: '/solutions/agency-project-management-software',
    },
    faq: [
      {
        q: 'Is the say no to a client reply template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What if the client asks for more work on the project itself?',
        a: 'That is not a no, it is a price. The template says to use a change request or a revisions reply instead.',
      },
      {
        q: 'How do I answer a client who asks for a discount?',
        a: 'Cut the job, not the rate. AIGA’s Standard Form of Agreement says reducing the scope of work reduces the overall price, so the template offers a smaller site and a phase two.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your terms checked where you work, especially anything about schedules, delays and fees.',
      },
    ],
    relatedLinks: [
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Web design revision policy template', path: '/templates/web-design-revision-policy' },
      { label: 'Difficult client reply scripts', path: '/templates/difficult-client-reply-scripts' },
      { label: 'Website scope of work template', path: '/templates/website-scope-of-work' },
      { label: 'How to stop scope creep on client projects', path: '/blog/how-to-stop-scope-creep-on-client-projects' },
    ],
  },
  {
    slug: 'client-ghosting-email-sequence',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Client ghosting email sequence',
    cardCopy: 'Agreement wording for client delays, three messages in order, a voicemail script, a restart reply and a tracker. PDF and Word.',
    h1: 'Free client ghosting email sequence (PDF and Word).',
    metaTitle: 'Client Ghosting Email Sequence: Free PDF & Word',
    description:
      'Download a free client ghosting email sequence in PDF and Word: agreement wording for client delays, three messages, a voicemail script and a tracker.',
    lede:
      'For when a web design client stops answering mid-project. Send a one-number reply, then a call and a second contact, then a plain pause notice, each with a date you will keep.',
    pages: 5,
    preview: {
      src: '/images/templates/client-ghosting-email-sequence-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the client ghosting email sequence: a title and intro, "Put this in your agreement before you need it" with four clauses, including ending the project, and the start of "The three messages at a glance".',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/client-ghosting-email-sequence-template.pdf', size: '136 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/client-ghosting-email-sequence-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Put this in your agreement before you need it', copy: 'Wording for client responsibilities, client delays, deadlines and ending the project, with notes from AIGA’s agreement guidance.' },
      { title: 'The three messages at a glance', copy: 'A table of when each message goes out, what it does and its tone. The order and gaps are a suggestion, not a rule.' },
      { title: 'Message 1: the number reply', copy: 'Lead with good news, ask one question answered with 1, 2 or 3 and give a hold date.' },
      { title: 'Message 2: the call, the voicemail and the second contact', copy: 'A fifteen-second voicemail script and a one-question email to someone else at the business.' },
      { title: 'Message 3: the pause notice', copy: 'When you last heard, what pauses and from when, the invoice stated once if one is due, and a way back. It says only what your agreement allows.' },
      { title: 'When they reply', copy: 'A same-day answer that offers the next free launch date without asking why it took so long.' },
      { title: 'Subject lines that work, and ones that don’t', copy: 'Three subject lines to skip and what to use instead.' },
      { title: 'Tracker', copy: 'A one-row table for the last reply, each message date, the call and the pause date.' },
      { title: 'Checklist before you send', copy: 'Eight checks, from the agreement wording to answering the same day.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio runs the three messages for a $4,800 site with a $2,400 balance due, with an invoice due on the 17th, a pause from the 20th and every date and sum checked. Use it to see what “specific” looks like; your numbers and dates will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Add the client delay line to your agreement before the next project starts, and have it checked where you work.',
      'Send message 1, then put every date in your calendar the same day.',
      'Call on the hold date, send the pause notice a few days later, say only what your agreement allows and answer the same day if they reply.',
    ],
    product: {
      copy: 'In Workspace369, an email workflow can send an overdue invoice for you and create a task so the phone call lands on your list. The pause notice and the call stay yours.',
      linkLabel: 'See Workflow Automations',
      path: '/features/automation',
    },
    faq: [
      {
        q: 'Is the client ghosting email sequence free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How many messages should I send before I stop?',
        a: 'The template uses three: a one-number reply, a call plus a second contact, and a pause notice. It says no official source gives a number or a gap, so treat the order as a suggestion.',
      },
      {
        q: 'What if the pause date passes with no reply?',
        a: 'You are ending the job, not chasing. The template says to send the exit email from the “how to fire a client” video instead of another chase.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Rules on pausing work, closing a project and holding files or a launch differ by country and state, so have your terms checked.',
      },
    ],
    relatedLinks: [
      { label: 'Client offboarding email template', path: '/templates/web-design-client-offboarding-email' },
      { label: 'Web design payment reminder emails template', path: '/templates/web-design-payment-reminder-emails' },
      { label: 'Web design proposal follow-up email template', path: '/templates/web-design-proposal-follow-up-emails' },
      { label: 'Web design contract template', path: '/templates/web-design-contract' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
    ],
  },
  {
    slug: 'referral-request-email',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Referral request email template',
    cardCopy: 'A request email, a handover-call line, a note to forward, a thank-you and one follow-up. PDF and Word.',
    h1: 'Free referral request email template (PDF and Word).',
    metaTitle: 'Referral Request Email Template: Free PDF & Word',
    description:
      'Download a free referral request email in PDF and Word: four rules for asking, the request email, a note to forward, a thank-you and one follow-up.',
    lede:
      'Ask a happy web design client for an introduction once the site is live and the final invoice is paid. Name the kind of owner you want, give them a note to forward and make a no easy.',
    pages: 4,
    preview: {
      src: '/images/templates/referral-request-email-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the referral request email template: a "Referral Request Email Template" title and intro, then "Before you send: four rules" as four bullets, and the start of "The request email" with a subject line and short body.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/referral-request-email-template.pdf', size: '90 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/referral-request-email-template.docx', size: '7 KB' },
    ],
    sections: [
      { title: 'Before you send: four rules', copy: 'Ask after the win, name who you want, make one ask and skip the reward, with the FTC’s CAN-SPAM guide and Endorsement Guides as sources.' },
      { title: 'The request email', copy: 'A short email sent once the final invoice is paid that names the kind of owner you want, includes a note to forward and says a no is fine.' },
      { title: 'The handover-call line', copy: 'One sentence to say out loud, plus a short text version.' },
      { title: 'The note they can forward', copy: 'Written in the client’s voice so they make the introduction themselves and you never email a stranger cold.' },
      { title: 'The reply when they can’t think of anyone', copy: 'A short, warm reply that leaves your note with them.' },
      { title: 'The thank-you', copy: 'Sent the same day, whether or not it turns into work.' },
      { title: 'One follow-up, then let it go', copy: 'A short nudge about a week later, then stop.' },
      { title: 'Checklist before you send', copy: 'Seven checks, from a paid final invoice to sending one follow-up only, and a note on remembering to ask.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio asks the client of a Harbor Dental site that launched five weeks ago, with a made-up note to forward. Use it to see what “named” looks like; your words will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Fill in the brackets, name the kind of owner you want to meet and delete the guidance in italics.',
      'Send the request once the final invoice is paid, with no discount or gift attached.',
      'Send the thank-you the same day, one follow-up about a week later if there is no reply, then let it go.',
    ],
    product: {
      copy: 'In Workspace369, every request records whether it came from a form, funnel or page, and forms can capture UTM parameters in hidden fields, on every plan, and tagging each client with a lead source starts on Voyager. You still write the email yourself.',
      linkLabel: 'See what Workspace369 tracks',
      path: '/workspace369-features',
    },
    faq: [
      {
        q: 'Is the referral request email template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'When should I ask a client for a referral?',
        a: 'After the win: the site is live, the final invoice is paid and the client has said thanks. The template says it wouldn’t ask while a balance is open.',
      },
      {
        q: 'Can I offer a reward for a referral?',
        a: 'The template says to skip the reward and say thanks after. The FTC’s CAN-SPAM guide says a seller that offers money or discounts for forwarding a message may be responsible for compliance, so check your local rules first.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Check the rules where you work, especially around referral rewards, forwarded commercial email and disclosing connections.',
      },
    ],
    relatedLinks: [
      { label: 'Testimonial request email template', path: '/templates/testimonial-request-email' },
      { label: 'Client offboarding email template', path: '/templates/web-design-client-offboarding-email' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
      { label: 'Client follow-up email guide', path: '/learn/client-follow-up-email' },
      { label: 'Web design client onboarding checklist template', path: '/templates/web-design-client-onboarding-checklist' },
    ],
  },
  {
    slug: 'website-project-timeline',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Website project timeline template',
    cardCopy: 'A kickoff day, a week-by-week table with your hours and your client’s dates, a slip rule and two emails. PDF and Word.',
    h1: 'Free website project timeline template (PDF and Word).',
    metaTitle: 'Website Project Timeline Template: Free PDF & Word',
    description:
      'Download a free website project timeline in PDF and Word: a kickoff day, a week-by-week table, a slip rule, the timeline email and a Friday status update.',
    lede:
      'Give a web design client a timeline that holds. Count the weeks from a named kickoff day, put your client’s dates on the page and say what happens to the ready date when one slips.',
    pages: 6,
    preview: {
      src: '/images/templates/website-project-timeline-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the website project timeline template: a title and intro, a project summary and kickoff day field table, and the start of the week-by-week table with weeks, stage, your hours and your client’s part columns.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/website-project-timeline-template.pdf', size: '142 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/website-project-timeline-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Project summary and kickoff day', copy: 'A field table for the client, kickoff day, what must happen first, length in weeks and the ready date, based on AIGA’s advice to plan in elapsed time.' },
      { title: 'The week-by-week table', copy: 'One row per stage, with your hours in one column and your client’s part in another so every row has an owner.' },
      { title: 'Your client’s dates', copy: 'Content due, feedback within a set number of working days, sign-off and days off.' },
      { title: 'When a date slips', copy: 'A slip rule that moves the ready date by the same number of working days, and a late-content note to send the day it happens.' },
      { title: 'The email that sends the timeline', copy: 'A short email with the kickoff date, the ready date and the two dates that are your client’s.' },
      { title: 'The Friday status update email', copy: 'What is done, what is next and what you need from the client by when.' },
      { title: 'Where the weeks and dates live', copy: 'Three checks for keeping the timeline, stages and status reminder in one shared place.' },
      { title: 'Checklist before you send', copy: 'Seven checks, from a written kickoff day to putting the first Friday update in your calendar.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio plans a six-week, 50-hour build for a seven-page, $4,800 site, kicking off Monday, January 11, 2027 and ready Friday, February 19, with a two-day slip that moves it to February 23 and a week 2 update, all made up. Use it to see how the hours and dates add up; yours will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Name the kickoff day and what starts the clock, then fill in the week-by-week table so the hours match your quote.',
      'Add your client’s dates and the slip rule, and have the wording checked where you work.',
      'Send the timeline email before kickoff, then send the Friday status update on the same day every week.',
    ],
    product: {
      copy: 'In Workspace369, projects, task boards and stages are on every plan, and a client portal lets clients follow their projects, task boards and invoices.',
      linkLabel: 'See agency project management',
      path: '/solutions/agency-project-management-software',
    },
    faq: [
      {
        q: 'Is the website project timeline template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'How long should a website project take?',
        a: 'The template found no official source for how long a website takes, so the weeks are suggestions to change. Its example uses six weeks, which the template says is made up and not typical.',
      },
      {
        q: 'What happens when my client is late with content?',
        a: 'The slip rule moves the ready date by the same number of working days, and you tell the client the new date the same day. AIGA says most design firms specify a day-for-day extension for a lengthy client delay.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Have your terms checked by someone qualified where you work, especially the slip rule.',
      },
    ],
    relatedLinks: [
      { label: 'Website scope of work template', path: '/templates/website-scope-of-work' },
      { label: 'Web design client onboarding checklist template', path: '/templates/web-design-client-onboarding-checklist' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Web design discovery call questions template', path: '/templates/web-design-discovery-call-questions' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
    ],
  },
  {
    slug: 'hourly-vs-fixed-price-calculator',
    updatedOn: '2026-10-19',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Hourly vs fixed price calculator',
    cardCopy: 'A fixed-or-hourly check, your hourly floor, a fixed-price calculator with break-even hours and the “offer both” email. PDF and Word.',
    h1: 'Free hourly vs fixed price calculator (PDF and Word).',
    metaTitle: 'Hourly vs Fixed Price Calculator: Free PDF & Word',
    description:
      'Download a free hourly vs fixed price calculator in PDF and Word: your hourly floor, break-even hours, quote wording and the “offer both” email.',
    lede:
      'Before you quote a web design job, work out your floor, price the hours you expect and write down the break-even point. After the job, the same page shows the rate you really earned.',
    pages: 6,
    preview: {
      src: '/images/templates/hourly-vs-fixed-price-calculator-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the hourly vs fixed price calculator: a title and intro, a note that it gives a method and not a market rate, the "Fixed or hourly?" check as three checkbox lines, and the start of "Your floor" with a table for pay, yearly costs and billable hours.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/hourly-vs-fixed-price-calculator-template.pdf', size: '152 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/hourly-vs-fixed-price-calculator-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Fixed or hourly?', copy: 'A three-point check for you: you have built something like it before, the scope fits on one page and the content is ready. If not, go hourly, labelled as an estimate.' },
      { title: 'Your floor', copy: 'The pay you want plus your yearly costs, divided by the hours you can really bill in a year: the hourly rate you don’t quote below.' },
      { title: 'The fixed-price calculator', copy: 'Expected hours times your rate gives the fixed price and the break-even hours. After the job, your tracked hours give your real rate and what hourly would have paid.' },
      { title: 'A fixed price with an hourly edge', copy: 'Quote wording with the rounds of changes included, the price of a further round, a content date and an hourly rate for anything outside the list.' },
      { title: 'The “offer both” email', copy: 'A short email that gives the client a fixed price and an hourly option with an hour-count check-in, so they choose.' },
      { title: 'Optional: a value check', copy: 'A table that starts from the client’s own number, such as hours a month the new site saves, and compares a slice of it with your floor.' },
      { title: 'Checklist before you send', copy: 'Nine checks, from knowing your floor and writing down the break-even hours to timing the job even when the price is fixed.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio with a $90 floor quotes a five-page site at $3,600 fixed for 40 hours. It takes 52, so the real rate falls to about $69 an hour, and the example shows what hourly would have paid, the same job with the hourly edge and a value check. Every number is made up, not a market rate.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs, Pages or a spreadsheet.',
      'Run the fixed-or-hourly check, then work out your floor from your own pay, costs and billable hours.',
      'Price the hours you expect, write down the break-even hours and send the quote with the hourly edge, or offer both.',
      'Time the job, then fill in the after-the-job table and use the real hours on your next quote.',
    ],
    product: {
      copy: 'In Workspace369, timers, billable time tracking and time-to-invoice are on every plan, so the hours you track can become invoice lines.',
      linkLabel: 'See time tracking in Workspace369',
      path: '/features/time-tracking',
    },
    faq: [
      {
        q: 'Is the hourly vs fixed price calculator free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'Should I charge hourly or a fixed price for a website?',
        a: 'Go fixed when you have built something like it before, the scope fits on one page and the content is ready. Go hourly when nobody can see the end of the job. This is a rule of thumb, not a standard. AIGA’s Standard Form of Agreement says fees may be on an hourly or a fixed-fee basis.',
      },
      {
        q: 'What are break-even hours?',
        a: 'The fixed price divided by your hourly rate. If the job takes longer than that, hourly would have paid more; if it takes less, the fixed price pays more.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information, not legal, tax or financial advice. Have your terms checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Web design price sheet template', path: '/templates/web-design-price-sheet' },
      { label: 'Web design quote template', path: '/templates/web-design-quote' },
      { label: 'Rate increase email template', path: '/templates/web-design-rate-increase-email' },
      { label: 'Hourly rate calculator', path: '/tools/hourly-rate-calculator' },
      { label: 'Web design pricing benchmarks', path: '/research/web-design-pricing-benchmarks' },
    ],
  },
  {
    slug: 'website-redesign-proposal',
    updatedOn: '2026-10-19',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Website redesign proposal template',
    cardCopy: 'A refresh-or-redesign check, a stays-and-changes page, a refresh quote, a redesign quote and an old-to-new address map. PDF and Word.',
    h1: 'Free website redesign proposal template (PDF and Word).',
    metaTitle: 'Website Redesign Proposal Template: Free PDF & Word',
    description:
      'Download a free website redesign proposal template in PDF and Word: a refresh-or-redesign check, two quotes, an old-to-new address map and the reply email.',
    lede:
      'When a client says their site looks tired, find out first whether they need a refresh or a redesign. This template quotes either one and keeps the old web addresses safe when they change.',
    pages: 6,
    preview: {
      src: '/images/templates/website-redesign-proposal-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the website redesign proposal template: a title and intro, a note that it gives a format and not a market rate, the "Refresh or redesign?" check as three questions, and the stays-and-changes table with "Stays the same" and "Changes" columns.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/website-redesign-proposal-template.pdf', size: '165 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/website-redesign-proposal-template.docx', size: '40 KB' },
    ],
    sections: [
      { title: 'Refresh or redesign?', copy: 'Three questions for you: does any web address change, do any words or photos change, does the menu or page structure change? Three no’s mean a refresh.' },
      { title: 'The stays-and-changes page', copy: 'A two-column table to attach to either quote, plus who supplies the words and what is not included.' },
      { title: 'The refresh quote', copy: 'A fixed-price quote for a new look on the same addresses, words and menu, with priced lines, payment, a valid-until date and how to accept.' },
      { title: 'The redesign quote', copy: 'A fixed-price quote for a new layout, menu and pages, with lines for the address list and redirects and a check after launch.' },
      { title: 'The old-to-new address map', copy: 'A table of old addresses, new addresses and permanent (301) redirects, built from the sitemap, your analytics and Search Console.' },
      { title: 'The reply email', copy: 'A short email with both options, one price each, the first payment and a valid-until date.' },
      { title: 'Checklist before you send', copy: 'Eight checks, from deciding refresh or redesign with the three questions to quoting a hosting or CMS move separately.' },
      { title: 'Acceptance', copy: 'A block to add under the quote if you use a link or a form: how to accept, the first payment and the quote number, client name and date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio quotes both options for a dental practice’s seven-page site. The client asks to merge two pages, which turns a refresh into a redesign, so the example shows the stays-and-changes page, both quotes and the redirects for the merged pages. Every number is made up, not a market rate.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Ask the three refresh-or-redesign questions, then fill in the stays-and-changes page with the client.',
      'Fill in the refresh quote, the redesign quote or both, and make sure the lines add up to each total.',
      'For a redesign, map every old address to its new page before launch, then check each redirect and the sitemap after it.',
    ],
    product: {
      copy: 'In Workspace369, a quote can go out as a proposal with priced lines, a valid-until date and the deposit taken when the client accepts. Proposals and deposits on acceptance are on every plan.',
      linkLabel: 'See quoting in Workspace369',
      path: '/features/quoting',
    },
    faq: [
      {
        q: 'Is the website redesign proposal template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What is the difference between a website refresh and a redesign?',
        a: 'In this template, a refresh keeps the web addresses, the content and the page structure and changes only the look. If any of those three change, it is a redesign. That is our rule, not an official definition.',
      },
      {
        q: 'Why does a redesign quote need an address map?',
        a: 'When pages move or merge, old addresses need permanent redirects to the new pages. Google’s site-move guidance says to keep redirects as long as possible, generally at least a year, and that a significant change to a site can cause rankings to fluctuate while it is recrawled.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Rules on quotes, deposits and website changes differ by country and state, so have your terms checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Web design quote template', path: '/templates/web-design-quote' },
      { label: 'Website scope of work template', path: '/templates/website-scope-of-work' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Website project timeline template', path: '/templates/website-project-timeline' },
    ],
  },
  {
    slug: 'web-design-retainer-agreement',
    updatedOn: '2026-10-19',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Web design retainer agreement template',
    cardCopy: 'A retainer-or-project check, the offer email, a one-page retainer agreement, the project reply and a month-three review. PDF and Word.',
    h1: 'Free web design retainer agreement template (PDF and Word).',
    metaTitle: 'Web Design Retainer Agreement Template: Free PDF & Word',
    description:
      'Download a free web design retainer agreement in PDF and Word: a retainer-or-project check, the offer email, a one-page agreement and a review email.',
    lede:
      'For the client who wants help after launch: offer a retainer or a project, put the hours, the fee and what counts on one page, and review it in month three.',
    pages: 4,
    preview: {
      src: '/images/templates/web-design-retainer-agreement-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the web design retainer agreement template: a title and intro, a note that it gives a format and not a market rate, the "Retainer or project?" check as three checkbox lines, and the start of the offer email with a retainer option and a project option.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/web-design-retainer-agreement-template.pdf', size: '109 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/web-design-retainer-agreement-template.docx', size: '39 KB' },
    ],
    sections: [
      { title: 'Retainer or project?', copy: 'A quick check for you: a retainer for small work that comes back every month, a project for work with an end and a finished result.' },
      { title: 'The offer email', copy: 'Two options for the client to answer with 1 or 2: a monthly retainer with its hours and fee, or a project quoted on request.' },
      { title: 'The one-page retainer agreement', copy: 'What the hours cover, hours and fee, unused hours, what is a project instead, extra hours, reply times, a review month, notice to end it and acceptance.' },
      { title: 'When the request is really a project', copy: 'A reply that quotes bigger work as its own fixed-price project, so the monthly hours stay for small edits.' },
      { title: 'The month-three review email', copy: 'An email that shows the hours used and suggests more or fewer hours for the next months.' },
      { title: 'Checklist before you send', copy: 'Seven checks, from a price on both options and a fee that equals hours times rate to notice and a review date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional studio offers a dental client four hours a month for $360 after launch, with hours that reset each month, a new page quoted as a $630 project and a month-three review. Every number is made up, not a market rate.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Run the retainer-or-project check, then send the offer email in the last week of your included support.',
      'If the client picks the retainer, fill in the one-page agreement, check that hours times rate equals the fee, and have it checked where you work.',
      'Quote anything bigger as its own project, and send the review email in month three.',
    ],
    product: {
      copy: 'In Workspace369, retainers and deposits, recurring invoices and time tracking are on every plan, so a monthly retainer can go out as a recurring invoice and you can see the hours used.',
      linkLabel: 'See retainers and recurring billing',
      path: '/features/retainers-and-recurring-billing',
    },
    faq: [
      {
        q: 'Is the web design retainer agreement template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What is the difference between a retainer and a project?',
        a: 'A retainer buys your time each month for small, recurring work such as edits and fixes. A project buys a finished thing, such as a new page or a redesign, at its own price. That is our rule of thumb, not a standard.',
      },
      {
        q: 'Should unused retainer hours roll over?',
        a: 'Either can work, as long as the agreement says which. The template’s pick is that hours reset each month, and the month-three review lets you raise or lower them.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information. Rules on fees, notice and refunds differ by country and state, so have your terms checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Website maintenance agreement template', path: '/templates/website-maintenance-agreement' },
      { label: 'Web design contract template', path: '/templates/web-design-contract' },
      { label: 'Website change request form template', path: '/templates/website-change-request-form' },
      { label: 'Retainer agreement template guide', path: '/learn/retainer-agreement-template' },
      { label: 'Website handover checklist template', path: '/templates/website-handover-checklist' },
    ],
  },
  {
    slug: 'freelance-contract',
    updatedOn: '2026-10-19',
    cardTitle: 'Freelance contract template',
    cardCopy: 'A plain-language services agreement for any freelancer: scope, fee options, deposit, ownership and a filled-in example. PDF and Word.',
    h1: 'Free freelance contract template (PDF and Word).',
    metaTitle: 'Freelance Contract Template: Free PDF & Word | Workspace369',
    description:
      'Download a free freelance contract template in PDF and Word: scope, timeline, fees, deposit, late payment, changes and ownership. No email needed.',
    lede:
      'A services agreement for designers, developers, writers, photographers and consultants, written in plain English. It sets out the work, the price, when you get paid and who owns the result. Fill in the brackets, pick your options and delete the guidance.',
    pages: 5,
    preview: {
      src: '/images/templates/freelance-contract-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the freelance contract template: the "Freelance Contract Template" title and italic how-to-use note, then a "Freelance Services Agreement" heading with client, freelancer, date and project lines, then section 1 "The work" with deliverables, revision rounds and what is not included.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/freelance-contract-template.pdf', size: '107 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/freelance-contract-template.docx', size: '8 KB' },
    ],
    sections: [
      { title: 'The work', copy: 'The deliverables, the number of revision rounds and a "not included" line, written so a stranger could tell when the job is finished.' },
      { title: 'Timeline and what the client provides', copy: 'Your dates, the client’s deadlines for materials and feedback, and a line that moves the schedule when those arrive late.' },
      { title: 'Fees and payment', copy: 'Pick a fixed fee, an hourly rate with an estimate, or a monthly retainer, then set the deposit and the number of days each invoice is due in.' },
      { title: 'Late payment', copy: 'When work pauses for an unpaid invoice, an optional late fee you check against local rules, and who pays third-party costs.' },
      { title: 'Changes', copy: 'Extra work or rounds get a written price and time first, and start only once the client approves it.' },
      { title: 'Who owns the work', copy: 'Either copyright passes to the client on full payment, or you keep it and grant a licence. You keep your tools, templates and earlier work.' },
      { title: 'Confidentiality and working relationship', copy: 'A short mutual confidentiality clause, and a line that you work as an independent business, not an employee.' },
      { title: 'Ending the agreement, liability and signatures', copy: 'Notice to end, payment for work done, an optional kill fee, a liability cap to have checked locally, and signature lines for both sides.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'The same agreement completed for a fictional freelance copywriter and a bakery: five web pages, two revision rounds, a $2,400 fixed fee with a 50% deposit, invoices due in 14 days and ownership passing on payment. Use it to see what “specific” looks like; your numbers will differ.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs, Pages and LibreOffice.',
      'Replace every [bracket] with your project’s details, pick one option wherever there is a choice and delete the italic guidance.',
      'Write a real payment date or a clear number of days that meets your local rule, and decide who owns the work before you start.',
      'Have it checked by a lawyer where you and your client work, then send it for both of you to sign and keep a signed copy each.',
    ],
    product: {
      copy: 'Sending contracts to clients every week? Workspace369 Contracts lets clients verify their email and sign online, and keeps the signed copy with its audit trail.',
      linkLabel: 'See Workspace369 Contracts',
      path: '/features/contracts',
    },
    faq: [
      {
        q: 'Is the freelance contract template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own client work.',
      },
      {
        q: 'What should a freelance contract include?',
        a: 'Who the two parties are, the work and what is not included, the timeline, the fee and how it is calculated, the deposit and when each invoice is due, what happens with late payment and changes, who owns the finished work, confidentiality, how either side can end the agreement, and both signatures.',
      },
      {
        q: 'Does the law require a written freelance contract?',
        a: 'It depends on where you work. In New York City, contracts worth $800 or more, including agreements with the same client that add up to $800 in any 120-day period, must be in writing and must state the work, the pay and the payment date, according to the city’s Department of Consumer and Worker Protection. New York State added the Freelance Isn’t Free Act (General Business Law Article 44-A) on August 28, 2024, with its own rules, according to the state Department of Labor. Illinois’ Freelance Worker Protection Act covers contracts signed after July 1, 2024 for work worth $500 or more over 120 days, done in Illinois or for an Illinois-based client, according to the Illinois Department of Labor. Check the rules where you and your client are.',
      },
      {
        q: 'Should my freelance contract say the work is “work made for hire”?',
        a: 'Usually not. Under US law, commissioned work by a freelancer counts as work made for hire only if it falls into one of nine categories and both sides sign a written agreement saying so, according to the US Copyright Office (Circular 30). For most freelance work, a written assignment of copyright on full payment, as in this template, is the clearer route.',
      },
      {
        q: 'Can I edit the template in Google Docs?',
        a: 'Yes. Upload the Word (.docx) file to Google Drive and open it with Google Docs. It also opens in Microsoft Word, Apple Pages and LibreOffice.',
      },
      {
        q: 'Is this template legal advice?',
        a: 'No. It is general information and a starting point. Laws on contracts, payment, late fees, ownership and liability differ by country, state and city, so have it checked by a lawyer where you and your client work before you rely on it.',
      },
    ],
    relatedLinks: [
      { label: 'Freelance contract generator', path: '/tools/freelance-contract-template' },
      { label: "NYC Freelance Isn't Free Act guide", path: '/blog/freelance-isnt-free-act' },
      { label: 'Graphic design contract clauses', path: '/learn/graphic-design-contract-template' },
      { label: 'Web design contract template', path: '/learn/web-design-contract-template' },
      { label: 'Retainer agreement template', path: '/learn/retainer-agreement-template' },
      { label: 'What is a kill fee?', path: '/learn/kill-fee' },
      { label: 'Deposit calculator', path: '/tools/deposit-calculator' },
    ],
    lawRules: {
      title: 'What freelancer laws require in the contract',
      intro:
        'New York City, New York State and Illinois have laws that set rules for freelance contracts. If one applies to you, check that your filled-in agreement covers what it asks for. This is general information from the official sources linked in each row, not legal advice.',
      checkedOn: '2026-10-19',
      rules: [
        {
          place: 'New York City',
          law: 'Freelance Isn’t Free Act (Local Law 140 of 2016)',
          inForce: 'May 15, 2017',
          threshold: 'Work worth $800 or more, counting every agreement with the same client that totals $800 in any 120-day period.',
          contract: 'In writing, setting out the work you will do, the pay for the work and the date you get paid.',
          payment: 'On or before the date in the contract. With no date, within 30 days after you complete the work.',
          sources: [
            { label: 'NYC Department of Consumer and Worker Protection', href: 'https://www.nyc.gov/site/dca/workers/workersrights/freelancer-workers.page' },
            { label: 'DCWP five-year report (2023)', href: 'https://www.nyc.gov/assets/dca/downloads/pdf/workers/DCWP-Freelance-Isnt-Free-Act-Five-YearReport-2023.pdf' },
          ],
        },
        {
          place: 'New York State',
          law: 'Freelance Isn’t Free Act (General Business Law Article 44-A)',
          inForce: 'August 28, 2024',
          threshold: 'Work worth $800 or more, alone or together with all contracts with the same client in the previous 120 days. Some sales representatives, lawyers, licensed medical professionals and construction contractors are excluded.',
          contract: 'In writing, with the name and mailing address of both sides; an itemized list of the services, their value, and the rate and method of pay; the payment date or how it will be set; and the date you must submit a list of the services you delivered. The client keeps the contract for at least six years.',
          payment: 'On or before the date in the contract. With no date, within 30 days after you complete the work.',
          sources: [
            { label: 'NY State Department of Labor', href: 'https://dol.ny.gov/node/54011' },
            { label: 'GBL §1410 (definitions)', href: 'https://www.nysenate.gov/legislation/laws/GBS/1410' },
            { label: 'GBL §1411 (payment)', href: 'https://www.nysenate.gov/legislation/laws/GBS/1411' },
            { label: 'GBL §1412 (written contracts)', href: 'https://www.nysenate.gov/legislation/laws/GBS/1412' },
          ],
        },
        {
          place: 'Illinois',
          law: 'Freelance Worker Protection Act',
          inForce: 'July 1, 2024 (contracts signed after that date)',
          threshold: 'Work worth $500 or more over a 120-day period, including several contracts with the same client. The work is done in Illinois or for an Illinois-based client. Construction workers, employees, and people contracted by federal, state, local or foreign governments are excluded.',
          contract: 'The name and contact information of both sides, an itemized list of the products and services, the rate and method of pay, the date payment is due, and the dates the services will be provided.',
          payment: 'By the due date in the contract. With no date, within 30 days of completing the services.',
          sources: [
            { label: 'Illinois Department of Labor', href: 'https://labor.illinois.gov/faqs/freelance-worker-protection-act.html' },
          ],
        },
      ],
      note: 'Other cities and states may have their own rules, and each law has exceptions and remedies not listed here. Read the official page for the place where you and your client work.',
    },
    retiredResources: {
      title: 'Where the free freelance contracts went',
      intro:
        'Four free contracts that older articles still link to are no longer there. Here is what each one was, what its old link shows today and an archived copy of the original.',
      checkedOn: '2026-10-19',
      items: [
        {
          name: 'Shake',
          oldUrl: 'shakelaw.com',
          was: 'A mobile app from Shake for creating, signing and sending short legal agreements from a phone.',
          now: 'The domain now shows an unrelated gambling site.',
          archiveHref: 'https://web.archive.org/web/20140105200847/http://www.shakelaw.com/',
          archivedOn: '2014-01-05',
        },
        {
          name: 'AND.CO “The Freelance Contract”',
          oldUrl: 'and.co/the-freelance-contract',
          was: 'A free standard service agreement from the freelance app AND.CO, presented as compliant with New York City’s Freelance Isn’t Free Act.',
          now: 'The link redirects to Fiverr.',
          archiveHref: 'https://web.archive.org/web/20210926104327/https://www.and.co/the-freelance-contract',
          archivedOn: '2021-09-26',
        },
        {
          name: 'Docracy',
          oldUrl: 'docracy.com',
          was: 'An open library of free contracts shared by its users, such as a “Contract of Works for Web Design”.',
          now: 'Contract links open a page saying Docracy is no longer available, which points to the eversign e-signature service.',
          archiveHref: 'https://web.archive.org/web/20210121051257/https://www.docracy.com/3884/contract-of-works-for-web-design',
          archivedOn: '2021-01-21',
        },
        {
          name: 'Contract Killer',
          oldUrl: 'gist.github.com/malarkey/4031110',
          was: 'Andy Clarke’s plain-English contract for web designers and developers, published as a public GitHub gist.',
          now: 'The gist returns “404 Not Found”.',
          archiveHref: 'https://web.archive.org/web/20240616084209/https://gist.github.com/malarkey/4031110',
          archivedOn: '2024-06-16',
        },
      ],
      note: 'The template on this page covers the same ground in one free download: the work, the fee and payment dates, late payment, changes, ownership and ending the agreement.',
    },
  },
  {
    slug: 'small-business-plan',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Small business plan template',
    cardCopy: 'A one-page lean plan, a short traditional plan, a numbers worksheet and a filled-in example for a service business. PDF and Word.',
    h1: 'Free small business plan template (PDF and Word).',
    metaTitle: 'Small Business Plan Template: Free PDF & Word | Workspace369',
    description:
      'Download a free small business plan template in PDF and Word for freelancers: a one-page lean plan, a nine-section plan, a numbers worksheet and a checklist.',
    lede:
      'A business plan for a freelancer or a small service business, in two sizes: a one-page lean plan that can take as little as an hour, and a short traditional plan for when a lender or a partner wants the detail. The sections follow the two formats the SBA describes.',
    pages: 6,
    preview: {
      src: '/images/templates/small-business-plan-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the small business plan template: a "Small Business Plan Template" title and intro, a note that Workspace369 is not affiliated with the SBA, the "Which plan do you need?" check as three checkbox lines, and a note on how long each format runs.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/small-business-plan-template.pdf', size: '176 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/small-business-plan-template.docx', size: '41 KB' },
    ],
    sections: [
      { title: 'Which plan do you need?', copy: 'A three-point check: a one-page lean plan for a simple business you are starting or testing, a traditional plan if you will ask a lender for money or bring in a partner.' },
      { title: 'The one-page lean plan', copy: 'Nine boxes, one or two lines each: value proposition, customer segments, channels, customer relationships, revenue streams, key activities, key resources, key partnerships and cost structure, with prompts written for service businesses.' },
      { title: 'The traditional plan', copy: 'Executive summary, company description, market analysis, organization and management, service line, marketing and sales, funding request, financial projections and appendix, each with prompts to fill in.' },
      { title: 'The numbers that matter', copy: 'A worksheet for monthly business costs, the pay you need, your average earnings per client and how many clients that takes, with a check that the arithmetic adds up.' },
      { title: 'Checklist before you use the plan', copy: 'Seven checks, from a one-sentence value proposition and a specific customer group to sourced market facts, checked licences and insurance, and a review date.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional solo bookkeeping service fills in the one-page lean plan, works out how many monthly clients covers its costs and the owner’s pay, and lists its next three steps. Every number is invented, not a guide to what to charge.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Run the check in section 1 and pick the one-page lean plan or the traditional plan.',
      'Fill in the numbers worksheet first, so the plan rests on your own costs, pay and prices.',
      'Write the plan, run the checklist and set a date to review it. In the U.S., SCORE mentors and Small Business Development Centers can go through it with you for free.',
    ],
    product: {
      copy: 'When the plan turns into clients, Workspace369 keeps clients, proposals, invoices and payments in one place for service businesses.',
      linkLabel: 'See what Workspace369 does',
      path: '/workspace369-features',
    },
    faq: [
      {
        q: 'Is the small business plan template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own business.',
      },
      {
        q: 'Should I write a lean plan or a traditional plan?',
        a: 'The SBA describes a lean plan as typically one page and a traditional plan as more detailed, and notes that lenders and investors commonly request a traditional plan. If the plan is mainly for you and the business is simple, start lean. If you will ask for a loan or bring in a partner, write the traditional plan, and ask your lender what it needs.',
      },
      {
        q: 'Is this the SBA business plan template?',
        a: 'No. Workspace369 is not affiliated with the SBA. The template follows the lean and traditional plan sections the SBA describes in its guide to writing a business plan, with prompts written for freelancers and small service businesses.',
      },
      {
        q: 'Does the template tell me what to charge or what my costs will be?',
        a: 'No. It gives you a structure and a worksheet, not numbers. Every figure in the example is made up. Use your own costs and prices, and ask an accountant about taxes.',
      },
      {
        q: 'Is this template legal or financial advice?',
        a: 'No. It is general information. Have your legal structure, licences, insurance and any funding request checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Freelance contract template', path: '/templates/freelance-contract' },
      { label: 'Pitch deck template', path: '/templates/pitch-deck' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Hourly rate calculator', path: '/tools/hourly-rate-calculator' },
      { label: 'Profit margin calculator', path: '/tools/profit-margin-calculator' },
      { label: 'Small business survival rate statistics', path: '/research/small-business-survival-rate-statistics' },
    ],
  },
  {
    slug: 'pitch-deck',
    updatedOn: '2026-10-18',
    sectionsOrder: 'in the order you use them',
    cardTitle: 'Pitch deck template',
    cardCopy: 'A slide-by-slide worksheet for a 10 to 12 slide pitch deck: the outline, what each slide must answer, a numbers check and a filled-in example. PDF and Word.',
    h1: 'Free pitch deck template (PDF and Word).',
    metaTitle: 'Pitch Deck Template: Free PDF & Word Outline | Workspace369',
    description:
      'Download a free pitch deck template in PDF and Word: a one-page outline of twelve slides, the questions each slide must answer and a numbers check.',
    lede:
      'A slide-by-slide worksheet for a startup or small business pitch: write the words first, then build the slides in PowerPoint, Keynote, Google Slides or Canva. The slide order draws on the sections Sequoia Capital lists, with Product, Traction and The ask added, and on Guy Kawasaki’s 10/20/30 rule.',
    pages: 6,
    preview: {
      src: '/images/templates/pitch-deck-template-page-1.webp',
      width: 1082,
      height: 1400,
      alt: 'First page of the pitch deck template: a "Pitch Deck Template" title and intro, a note that Workspace369 is not affiliated with Sequoia Capital or Guy Kawasaki, and the "Who is the deck for?" check with four checkbox lines for investors, a lender or grant panel, a pitch competition and a partner.',
    },
    files: [
      { format: 'pdf', label: 'Download PDF', href: '/downloads/templates/pitch-deck-template.pdf', size: '192 KB' },
      { format: 'docx', label: 'Download Word (.docx)', href: '/downloads/templates/pitch-deck-template.docx', size: '41 KB' },
    ],
    sections: [
      { title: 'Who is the deck for?', copy: 'A four-way check for investors, a lender or grant panel, a pitch competition or a partner, with what each room needs to hear first, plus the time you have and the next step you want.' },
      { title: 'The one-page outline', copy: 'Twelve slides in order, from title, problem and solution to traction, team, financials and the ask, with the question each one answers and a line for your answer.' },
      { title: 'What each slide must answer', copy: 'Prompts for every slide, such as how customers cope today, how to count your market bottom-up, what counts as traction, and a table for competitors and alternatives.' },
      { title: 'The numbers check', copy: 'Market size, monthly revenue, use of funds and runway in one table, so every number on your slides agrees.' },
      { title: 'Checklist before you pitch', copy: 'Seven checks, from one idea per slide and readable text to a timed run-through, backup slides for questions and a PDF that reads without you.' },
    ],
    example: {
      title: 'Plus a filled-in example',
      copy: 'A fictional café scheduling startup fills in all twelve slides in one line each and checks its market, revenue, use of funds and runway. Every number is invented.',
    },
    steps: [
      'Download the PDF to read it, or the Word file to edit it. The Word file also opens in Google Docs and Pages.',
      'Tick who the deck is for in section 1 and write down how long you have.',
      'Fill in the one-page outline, then answer the prompts for each slide in section 3. Those become your speaker notes.',
      'Run the numbers check and the checklist, then build the slides with one idea on each.',
    ],
    product: {
      copy: 'When the pitch turns into customers, Workspace369 keeps clients, proposals, invoices and payments in one place for service businesses.',
      linkLabel: 'See what Workspace369 does',
      path: '/workspace369-features',
    },
    faq: [
      {
        q: 'Is the pitch deck template free?',
        a: 'Yes. Download the PDF or the Word file without an account or an email address, and use it for your own pitch.',
      },
      {
        q: 'Is this a slide template?',
        a: 'No. It is a worksheet for the words and numbers behind each slide. Fill it in first, then build the slides in PowerPoint, Keynote, Google Slides or Canva, with one idea on each slide.',
      },
      {
        q: 'How many slides should a pitch deck have?',
        a: 'Guy Kawasaki’s 10/20/30 rule says a pitch should have ten slides, last no more than twenty minutes and use no font smaller than thirty points. The template has twelve slides and shows which two to combine to get to ten. If a pitch competition sets its own limit, follow that.',
      },
      {
        q: 'Is this the Sequoia pitch deck template?',
        a: 'No. Workspace369 is not affiliated with Sequoia Capital or Guy Kawasaki. The slide order draws on the sections Sequoia lists in “Writing a Business Plan”, with Product, Traction and The ask added, plus prompts and a numbers check.',
      },
      {
        q: 'Is this template legal or financial advice?',
        a: 'No. It is general information. Have any investment terms, loan or grant application checked by someone qualified where you work.',
      },
    ],
    relatedLinks: [
      { label: 'Small business plan template', path: '/templates/small-business-plan' },
      { label: 'Web design proposal template', path: '/templates/web-design-proposal' },
      { label: 'Profit margin calculator', path: '/tools/profit-margin-calculator' },
      { label: 'Hourly rate calculator', path: '/tools/hourly-rate-calculator' },
      { label: 'Small business survival rate statistics', path: '/research/small-business-survival-rate-statistics' },
    ],
  },
];

export const freeTemplatesBySlug: Record<string, FreeTemplate> = Object.fromEntries(
  freeTemplates.map((template) => [template.slug, template]),
);
